import React, { useEffect, useMemo, useState } from 'react';
import People from './email/people/People';
import {
  BarList, Btn, DangerBtn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select,
  SubTabs, TextInput, fmtDate, panelGet, panelPost, useAdminGet,
} from './kit';
import EditableTemplates from './email/EditableTemplates';
import SendToUsers, { EMAIL_PEOPLE_EVENT } from './email/SendToUsers';
import LinkClicks from './email/links/LinkClicks';
import DesignLibrary from './email/designs/DesignLibrary';
import TemplateLibrary from './email/library/TemplateLibrary';
import { EMAIL_USE_DESIGN_EVENT } from './email/designs/types';
import Timeline from './email/people/Timeline';
import type { TimelineTarget } from './email/people/shared';
import DeliveryTrace, { type TracePreset, filedText } from './email/trace/DeliveryTrace';
import EmailDashboard, { type DashboardPreset } from './email/dashboard/EmailDashboard';
import InvitesPanel, { type InvitesPreset } from './email/invites/InvitesPanel';
import MemacPanel from './email/memac/MemacPanel';
import { AdminLink, openAdmin, type AdminTargetIn } from './adminNav';

/**
 * Email Center — the whole of the legacy Streamlit email tooling in one panel.
 *
 * Failed tracked sends, the university-contact threads and the SendGrid delivery
 * report came first; the rest is the ported admin_v3 email_tools: the campaign
 * composer (university → researcher picker with the already-emailed suppression
 * list), the sent log with its delivery-status refresh and danger zone, the
 * campaign template manager, the CSV/XLSX mail-merge, the SendGrid Email Logs
 * table, per-message tracking, the conversation browser, the editable
 * customer-template store (email/EditableTemplates — every customer e-mail's
 * subject, sender, body and buttons, above the read-only platform catalogue),
 * sending one of those templates to a filtered group of users
 * (email/SendToUsers), and the OTP reproduction tools.
 *
 * Every send goes through an explicit operator confirmation: the backend refuses
 * anything without confirm=true (and the literal word CLEAR for the log wipe),
 * and that flag is only ever set from a control the operator ticked.
 *
 * Deep links (admin/adminNav.tsx, 2026-10-05): /admin?group=email&tab=<tab> —
 * the snake_case of a tab label (people, campaigns, deliverability, trace, …)
 * or the label itself — plus: trace → email, msg, incident (Delivery trace);
 * people → email or user (opens that timeline); campaigns → campaign=<send_id>
 * (opens it); deliverability → q (the incidents filter); dashboard (alias
 * stats) → template, mode, category, view (overview, weekly_table,
 * by_template, campaigns, individual_sends), q / status (open Individual
 * sends with that search / outcome), bulk=<group id> (narrows every view to
 * that bulk group); invites → run=<run id> (opens that invite run),
 * bulk=<group id> (narrows to the invites to that group; either one alone
 * opens Invites); memac (alias triggers) → trigger=<id> (opens its editor), view (overview, triggers, templates, runs, settings),
 * template=<key> (opens MEMAC → Templates on it); templates and saved_templates (alias library) → template=<key> (opens
 * it; a saved lib_… key always opens under Saved templates).
 *
 * Dashboard (email/dashboard/*, owner 2026-10-11) is the stats view over every
 * member e-mail: weekly per template (the MEMAC analytics table), per
 * campaign, per send. MEMAC (email/memac/*) is the lifecycle e-mail matrix:
 * statuses × cohorts, the triggers (all off until an admin turns one on),
 * their templates, runs and settings. Saved templates (email/library/*,
 * 2026-10-14) is the template library Campaigns and MEMAC share. Invites
 * (email/invites/*, 2026-10-15) is the invitation e-mails from Users → Bulk
 * add and Users → Send invites, run by run.
 */

// 'Campaigns' is the cohort sender (email/SendToUsers: filters or picked
// people, template or written from scratch, funnel); the older researcher
// outreach composer is 'Researcher outreach'. Dashboard, Invites and MEMAC sit
// after People, which stays the landing tab (the dashboard's reads are heavier).
const TRACE = 'Delivery trace';
const DASHBOARD = 'Dashboard';
const INVITES = 'Invites';
const SAVED = 'Saved templates';
const TABS = ['People', DASHBOARD, INVITES, 'MEMAC', 'Campaigns', 'Email designs', SAVED, 'Link clicks',
  'Deliverability', TRACE, 'Failed sends', 'Conversations', 'Tracking', 'Delivery report', 'Researcher outreach', 'Sent log',
  'Campaign templates', 'Manual send', 'Bulk mail-merge', 'SendGrid logs', 'Templates (editable)', 'OTP tools'];
const snake = (t: string) => t.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
/** Deep-link tab names → tabs: each label's snake_case, plus short aliases. */
const TAB_PARAM: Record<string, string> = {
  ...Object.fromEntries(TABS.map(t => [snake(t), t])),
  trace: TRACE, templates: 'Templates (editable)', designs: 'Email designs', otp: 'OTP tools',
  stats: DASHBOARD, email_stats: DASHBOARD, triggers: 'MEMAC', lifecycle_emails: 'MEMAC',
  library: SAVED, template_library: SAVED,
  invite_runs: INVITES, invite_tracking: INVITES,
};

type Note = { kind: 'error' | 'ok'; text: string } | null;
type Linked = { id: string; nonce: string };

const EmailPanel: React.FC<{ target?: AdminTargetIn }> = ({ target }) => {
  const [tab, setTab] = useState(TABS[0]);
  // The trace stays mounted once opened, so flipping to Deliverability and
  // back keeps the address being looked at.
  const [traceSeen, setTraceSeen] = useState(false);
  // The dashboard too: its filters survive a look at another tab.
  const [dashSeen, setDashSeen] = useState(false);
  const [dashPreset, setDashPreset] = useState<DashboardPreset | null>(null);
  // Invites too (period, group and the open run survive a look elsewhere).
  const [invSeen, setInvSeen] = useState(false);
  const [invPreset, setInvPreset] = useState<InvitesPreset | null>(null);
  const [memacPreset, setMemacPreset] = useState<{ trigger?: string; view?: string; template?: string; nonce: string } | null>(null);
  const [tracePreset, setTracePreset] = useState<TracePreset | null>(null);
  const [delivPreset, setDelivPreset] = useState<{ q: string; days: string; nonce: string } | null>(null);
  const [campaign, setCampaign] = useState<Linked | null>(null);
  // ?template=<key> for Templates (editable) and Saved templates.
  const [tplLink, setTplLink] = useState<string>('');
  const [timeline, setTimeline] = useState<TimelineTarget | null>(null);
  const show = (t: string) => {
    setTab(t);
    if (t === TRACE) setTraceSeen(true);
    if (t === DASHBOARD) setDashSeen(true);
    if (t === INVITES) setInvSeen(true);
  };
  // A sub-tab click drops one-off deep-link state, so a revisit starts clean.
  const choose = (t: string) => { setDelivPreset(null); setCampaign(null); setMemacPreset(null); setTplLink(''); show(t); };

  useEffect(() => {
    if (!target) return;
    const p = (k: string) => String(target[k] ?? '').trim();
    const raw = p('tab');
    let t = TABS.includes(raw) ? raw : TAB_PARAM[snake(raw)] || '';
    if (!t) {
      t = p('msg') || p('incident') ? TRACE : p('campaign') ? 'Campaigns' : p('run') || p('bulk') ? INVITES
        : p('email') || p('user') ? 'People' : '';
    }
    if (!t) return;
    if (t === TRACE) setTracePreset({ email: p('email'), msg: p('msg'), incident: p('incident'), nonce: target.nonce });
    if (t === 'People' && (p('email') || p('user'))) {
      const user = p('user');
      setTimeline(user.includes('@') ? { email: user.toLowerCase() }
        : { email: p('email').toLowerCase() || undefined, userId: user || undefined });
    }
    if (t === 'Campaigns' && p('campaign')) setCampaign({ id: p('campaign'), nonce: target.nonce });
    if (t === 'Deliverability' && (p('q') || p('days'))) setDelivPreset({ q: p('q'), days: p('days'), nonce: target.nonce });
    if (t === DASHBOARD && ['template', 'mode', 'category', 'view', 'q', 'status', 'bulk'].some(k => p(k))) {
      setDashPreset({ template: p('template') || undefined, mode: p('mode') || undefined,
        category: p('category') || undefined, view: p('view') || undefined, q: p('q') || undefined,
        status: p('status') || undefined, group: p('bulk') || undefined, nonce: target.nonce });
    }
    if (t === INVITES && (p('run') || p('bulk'))) {
      setInvPreset({ run: p('run') || undefined, bulk: p('bulk') || undefined, nonce: target.nonce });
    }
    if (t === 'MEMAC' && (p('trigger') || p('view') || p('template'))) {
      setMemacPreset({ trigger: p('trigger') || undefined, view: p('view') || undefined,
        template: p('template') || undefined, nonce: target.nonce });
    }
    if ((t === 'Templates (editable)' || t === SAVED) && p('template')) {
      // A saved template is edited under Saved templates, whichever tab the link named.
      if (p('template').startsWith('lib_')) t = SAVED;
      setTplLink(p('template'));
    }
    show(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.nonce]);
  // People → "E-mail these": open Campaigns (SendToUsers holds the event
  // until it mounts and preselects those people).
  useEffect(() => {
    const on = () => setTab('Campaigns');
    window.addEventListener(EMAIL_PEOPLE_EVENT, on);
    window.addEventListener(EMAIL_USE_DESIGN_EVENT, on);
    return () => {
      window.removeEventListener(EMAIL_PEOPLE_EVENT, on);
      window.removeEventListener(EMAIL_USE_DESIGN_EVENT, on);
    };
  }, []);
  const cfg = useAdminGet('email', 'send_config');
  const c: any = cfg.data;

  return (
    <div className="flex flex-col gap-4">
      <SubTabs tabs={TABS} active={tab} onChange={choose} />
      {cfg.error && <Msg kind="error">{cfg.error}</Msg>}
      {c && !c.sendgrid_configured && (
        <Msg kind="error">
          No SendGrid key is configured on the server — every send below will be refused.
          Previews, logs, tracking and the catalogues still work.
        </Msg>
      )}
      {tab === 'People' && <People />}
      {dashSeen && <div className={tab === DASHBOARD ? '' : 'hidden'}><EmailDashboard preset={dashPreset} /></div>}
      {invSeen && <div className={tab === INVITES ? '' : 'hidden'}><InvitesPanel preset={invPreset} /></div>}
      {tab === 'MEMAC' && <MemacPanel {...(memacPreset || {})} />}
      {tab === 'Deliverability' && <Deliverability preset={delivPreset} />}
      {traceSeen && <div className={tab === TRACE ? '' : 'hidden'}><DeliveryTrace preset={tracePreset} /></div>}
      {tab === 'Failed sends' && <FailedSends />}
      {tab === 'Conversations' && <Conversations />}
      {tab === 'Tracking' && <Tracking />}
      {tab === 'Delivery report' && <Report />}
      {tab === 'Researcher outreach' && <Composer cfg={c} />}
      {tab === 'Sent log' && <SentLog />}
      {tab === 'Campaign templates' && <TemplateManager />}
      {tab === 'Manual send' && <ManualSend cfg={c} />}
      {tab === 'Bulk mail-merge' && <BulkMerge cfg={c} />}
      {tab === 'SendGrid logs' && <SendGridLogs />}
      {tab === 'Templates (editable)' && <><EditableTemplates initialKey={tplLink || undefined} /><TransactionalTemplates /></>}
      {tab === 'Campaigns' && <SendToUsers linked={campaign} />}
      {tab === 'Link clicks' && <LinkClicks />}
      {tab === 'Email designs' && <DesignLibrary />}
      {tab === SAVED && <TemplateLibrary origin="library" initialKey={tplLink || undefined} />}
      {tab === 'OTP tools' && <OtpTools />}
      {timeline && <Timeline {...timeline} onClose={() => setTimeline(null)} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Local atoms (kit owns the shared ones; these are form-shaped and only used here)
// ---------------------------------------------------------------------------
const Field: React.FC<{
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; type?: string; span2?: boolean;
}> = ({ label, value, onChange, placeholder, type = 'text', span2 }) => (
  <label className={`flex flex-col gap-1 text-xs text-grayscale-1100 ${span2 ? 'col-span-2' : ''}`}>
    {label}
    <input type={type} value={value} placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300" />
  </label>
);

const Area: React.FC<{
  label: string; value: string; onChange: (v: string) => void;
  rows?: number; placeholder?: string; mono?: boolean;
}> = ({ label, value, onChange, rows = 8, placeholder, mono }) => (
  <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
    {label}
    <textarea value={value} rows={rows} placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      className={`px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300 ${mono ? 'font-mono text-xs' : ''}`} />
  </label>
);

const Check: React.FC<{
  checked: boolean; onChange: (v: boolean) => void; children: React.ReactNode;
}> = ({ checked, onChange, children }) => (
  <label className="flex items-center gap-2 text-sm text-grayscale-1200 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
      className="accent-primary-500" />
    {children}
  </label>
);

/**
 * The send gate. The button that actually mails people is only armed once the
 * operator ticks the box, and it is still a two-step DangerBtn after that — the
 * confirm=true the backend demands is never sent implicitly.
 */
const SendGate: React.FC<{
  label: string; consent: string; checked: boolean; onChange: (v: boolean) => void;
  blocked?: string; busy?: boolean; onSend: () => void;
}> = ({ label, consent, checked, onChange, blocked, busy, onSend }) => (
  <div className="flex flex-col gap-2 border-t border-grayscale-300 pt-3">
    <Check checked={checked} onChange={onChange}>{consent}</Check>
    {blocked
      ? <div className="text-xs text-grayscale-1100">{blocked}</div>
      : checked && !busy
        ? <div><DangerBtn onConfirm={onSend}>{label}</DangerBtn></div>
        : <div><Btn kind="danger" disabled>{busy ? 'Sending…' : label}</Btn></div>}
  </div>
);

const Pre: React.FC<{ children: string }> = ({ children }) => (
  <pre className="text-xs text-grayscale-1300 bg-grayscale-100 border border-grayscale-300 rounded-lg p-3 whitespace-pre-wrap break-words max-h-80 overflow-y-auto">
    {children}
  </pre>
);

const Drawer: React.FC<{
  title: string; subtitle?: string; onClose: () => void; wide?: boolean; children: React.ReactNode;
}> = ({ title, subtitle, onClose, wide, children }) => (
  <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={onClose}>
    <div className={`bg-grayscale-100 w-full ${wide ? 'max-w-3xl' : 'max-w-2xl'} h-full overflow-y-auto p-6 flex flex-col gap-4`}
      onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-grayscale-1300 font-grotesk truncate">{title}</h2>
          {subtitle && <div className="text-xs text-grayscale-1100">{subtitle}</div>}
        </div>
        <Btn onClick={onClose}>Close</Btn>
      </div>
      {children}
    </div>
  </div>
);

/** Base64 (data: URL) of a picked file — the backend tolerates the data: prefix. */
const readB64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const fr = new FileReader();
  fr.onload = () => resolve(String(fr.result || ''));
  fr.onerror = () => reject(fr.error || new Error('Could not read the file'));
  fr.readAsDataURL(file);
});

/** "1-50, 73" (1-based, as shown in the table) → zero-based row indices. */
const parseRanges = (spec: string, max: number): number[] => {
  const s = spec.trim();
  if (!s) return [];
  const out = new Set<number>();
  s.split(',').forEach(part => {
    const m = part.trim().match(/^(\d+)\s*-\s*(\d+)$/);
    if (m) {
      for (let i = Number(m[1]); i <= Number(m[2]); i++) if (i >= 1 && i <= max) out.add(i - 1);
    } else {
      const n = Number(part.trim());
      if (Number.isFinite(n) && n >= 1 && n <= max) out.add(n - 1);
    }
  });
  return Array.from(out).sort((a, b) => a - b);
};

const downloadJson = (name: string, payload: any) => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }));
  a.download = `${name}_${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const bars = (obj: any) => Object.entries(obj || {})
  .map(([k, v]) => ({ label: k || 'unknown', value: Number(v) || 0 }))
  .sort((a, b) => b.value - a.value);

// ---------------------------------------------------------------------------
const FailedSends: React.FC = () => {
  const failures = useAdminGet('email', 'failures');
  const [msg, setMsg] = useState<Note>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const retry = async (id: string, to: string) => {
    setBusy(id); setMsg(null);
    const { data, error } = await panelPost('email', 'retry_failure', { failure_id: id });
    setBusy(null);
    if (error || data?.success === false) setMsg({ kind: 'error', text: error || data?.error || 'Retry failed' });
    else { setMsg({ kind: 'ok', text: `Resent to ${to} — failure cleared.` }); failures.reload(); }
  };

  return (
    <Section title="Failed tracked-email sends" right={<Btn small onClick={failures.reload}>Refresh</Btn>}>
      <p className="text-xs text-grayscale-1100 -mt-1">
        The user's message was preserved when SendGrid failed; Retry re-sends it verbatim and
        clears the failure on success.
      </p>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      {failures.error && <Msg kind="error">{failures.error}</Msg>}
      {failures.loading ? <Loading /> : (
        <DataTable rows={failures.data || []} searchable csvName="email_send_failures"
          columns={['created_at', 'user_email', 'to_email', 'subject', 'error']}
          empty="No failed sends — everything delivered."
          actions={r => (
            <Btn small kind="primary" disabled={busy === r.id}
              onClick={() => retry(r.id, r.to_email)}>
              {busy === r.id ? 'Sending…' : 'Retry'}
            </Btn>
          )} />
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Conversations — email_conversations browser + the user_university_contacts
// thread table that used to be its own tab.
// ---------------------------------------------------------------------------
const Conversations: React.FC = () => {
  const [window_, setWindow] = useState('100');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('All');
  const [user, setUser] = useState('All');
  const [participant, setParticipant] = useState('All');
  const [convId, setConvId] = useState('All');
  const [open, setOpen] = useState<string | null>(null);

  const q = useAdminGet('email', 'conversations',
    { window: window_, search, status, user, participant_email: participant, conversation_id: convId },
    [window_, search, status, user, participant, convId]);
  const d: any = q.data;
  const facets = d?.facets || {};

  return (
    <div className="flex flex-col gap-4">
      {d && (
        <KPIRow>
          <KPI label="Conversations" value={d.metrics?.conversations ?? 0}
            hint={`of ${d.total_window ?? 0} in window`} />
          <KPI label="Messages" value={d.metrics?.messages ?? 0} />
          <KPI label="Active" value={d.metrics?.active ?? 0} />
          <KPI label="Archived" value={d.metrics?.archived ?? 0} />
          <KPI label="Deleted" value={d.metrics?.deleted ?? 0} />
        </KPIRow>
      )}
      <Section title="University-contact conversations"
        right={<Btn small onClick={q.reload}>Refresh</Btn>}>
        <div className="flex gap-2 flex-wrap items-center">
          <TextInput value={search} onChange={setSearch}
            placeholder="Search subject / participant / user / conversation id…" wide />
          <Select value={window_} onChange={setWindow} options={['50', '100', '200', '500']}
            labels={{ '50': 'Newest 50', '100': 'Newest 100', '200': 'Newest 200', '500': 'Newest 500' }} />
          <Select value={status} onChange={setStatus} options={facets.statuses || ['All']} />
          <Select value={user} onChange={setUser} options={['All', ...(facets.users || [])]} />
          <Select value={participant} onChange={setParticipant}
            options={['All', ...(facets.participants || [])]} />
          <Select value={convId} onChange={setConvId}
            options={['All', ...(facets.conversation_ids || [])]} />
        </div>
        {q.error && <Msg kind="error">{q.error}</Msg>}
        {q.loading ? <Loading /> : (
          <DataTable rows={d?.rows || []} csvName="email_conversations"
            columns={['created_at', 'conversation_id', 'user_display', 'participant_email',
              'subject', 'status', 'message_count', 'reference_count', 'utech_technology_id',
              'updated_at']}
            labels={{ user_display: 'User', reference_count: 'Refs' }}
            empty="No conversations match these filters."
            actions={r => <Btn small onClick={() => setOpen(r.conversation_id)}>Messages</Btn>} />
        )}
        {d && Object.keys(d.active_filters || {}).length > 0 && (
          <div className="text-xs text-grayscale-1100">
            Filtered by {Object.entries(d.active_filters).map(([k, v]) => `${k}=${v}`).join(' · ')}
            {' '}— {d.filtered} of {d.total_window} loaded.
          </div>
        )}
      </Section>
      {d && (
        <div className="grid md:grid-cols-2 gap-4">
          <Section title="Status breakdown">
            {(d.status_breakdown || []).length
              ? <BarList items={d.status_breakdown} />
              : <div className="text-sm text-grayscale-1100">Nothing to break down.</div>}
          </Section>
          <Section title="Top participants">
            {(d.top_participants || []).length
              ? <BarList items={d.top_participants} />
              : <div className="text-sm text-grayscale-1100">No participants in this slice.</div>}
          </Section>
        </div>
      )}
      <ContactThreads />
      {open && <ConversationDrawer conversationId={open} onClose={() => setOpen(null)} />}
    </div>
  );
};

const ConversationDrawer: React.FC<{ conversationId: string; onClose: () => void }> =
  ({ conversationId, onClose }) => {
  const [limit, setLimit] = useState('10');
  const [offset, setOffset] = useState(0);
  const q = useAdminGet('email', 'conversation_messages',
    { conversation_id: conversationId, limit, offset }, [conversationId, limit, offset]);
  const d: any = q.data;
  const rows: any[] = d?.rows || [];
  const total = d?.total ?? 0;

  return (
    <Drawer title={conversationId} onClose={onClose} wide
      subtitle={`${total} message(s) in this thread`}>
      {q.error && <Msg kind="error">{q.error}</Msg>}
      {(d?.by_type || []).length > 0 && (
        <Section title="Messages by type"><BarList items={d.by_type} /></Section>
      )}
      <Section title="Messages"
        right={<div className="flex gap-2 items-center">
          <Select value={limit} onChange={v => { setLimit(v); setOffset(0); }}
            options={['3', '10', '25', '50']}
            labels={{ '3': '3 per page', '10': '10 per page', '25': '25 per page', '50': '50 per page' }} />
          <Btn small disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - Number(limit)))}>Newer</Btn>
          <Btn small disabled={offset + Number(limit) >= total}
            onClick={() => setOffset(offset + Number(limit))}>Older</Btn>
        </div>}>
        {q.loading ? <Loading /> : rows.length === 0
          ? <div className="text-sm text-grayscale-1100 py-4 text-center">No messages stored for this conversation.</div>
          : (
            <div className="flex flex-col gap-3">
              {rows.map(m => (
                <div key={m.id} className="border border-grayscale-300 rounded-xl p-3 bg-white flex flex-col gap-1">
                  <div className="flex justify-between gap-3 flex-wrap text-xs text-grayscale-1100">
                    <span className="font-bold text-primary-700 uppercase">{m.message_type || 'unknown'}</span>
                    <span>{fmtDate(m.created_at)}</span>
                  </div>
                  <div className="text-sm text-grayscale-1300 font-bold">{m.subject || '(no subject)'}</div>
                  <div className="text-xs text-grayscale-1100">
                    {m.from_email || '—'} → {m.to_email || '—'}
                    {m.is_reply ? ' · reply' : ''}{m.has_html ? ' · has HTML part' : ''}
                    {m.sendgrid_message_id ? ` · ${m.sendgrid_message_id}` : ''}
                  </div>
                  <Pre>{m.content_text || m.preview || '(no text body stored)'}</Pre>
                </div>
              ))}
            </div>
          )}
      </Section>
    </Drawer>
  );
};

const ContactThreads: React.FC = () => {
  const [emailStatus, setEmailStatus] = useState('');
  const [contactStatus, setContactStatus] = useState('');
  const contacts = useAdminGet('email', 'contacts',
    { email_status: emailStatus, contact_status: contactStatus },
    [emailStatus, contactStatus]);
  return (
    <Section title="University-contact threads (all users)"
      right={<div className="flex gap-2">
        <Select value={emailStatus} onChange={setEmailStatus}
          options={['', 'sent', 'delivered', 'opened', 'clicked', 'bounced', 'failed']}
          labels={{ '': 'Any email status' }} />
        <Select value={contactStatus} onChange={setContactStatus}
          options={['', 'contacted', 'responded', 'no_response']}
          labels={{ '': 'Any contact status' }} />
        <Btn small onClick={contacts.reload}>Refresh</Btn>
      </div>}>
      {contacts.error && <Msg kind="error">{contacts.error}</Msg>}
      {contacts.loading ? <Loading /> : (
        <DataTable rows={contacts.data || []} searchable csvName="university_contacts"
          columns={['last_message_at', 'from_user', 'to_email', 'subject', 'email_status',
            'contact_status', 'utech_technology_id']}
          empty="No university-contact threads match." />
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
const Tracking: React.FC = () => {
  const [type, setType] = useState('All');
  const [limit, setLimit] = useState('1000');
  const q = useAdminGet('email', 'tracking', { email_type: type, limit }, [type, limit]);
  const d: any = q.data;

  return (
    <div className="flex flex-col gap-4">
      {d && (
        <KPIRow>
          <KPI label="Messages" value={d.metrics?.total ?? 0} />
          <KPI label="Sent" value={d.metrics?.sent ?? 0} />
          <KPI label="Delivered" value={d.metrics?.delivered ?? 0} />
          <KPI label="Opened" value={d.metrics?.opened ?? 0} />
          <KPI label="Clicked" value={d.metrics?.clicked ?? 0} />
          <KPI label="Bounced" value={d.metrics?.bounced ?? 0} />
          <KPI label="Failed" value={d.metrics?.failed ?? 0} />
        </KPIRow>
      )}
      <Section title="Per-message delivery tracking"
        right={<div className="flex gap-2 items-center">
          <Select value={type} onChange={setType}
            options={d?.type_options || ['All', 'University Contact', 'Invitation']} />
          <Select value={limit} onChange={setLimit} options={['200', '1000', '5000']}
            labels={{ '200': 'Latest 200', '1000': 'Latest 1000', '5000': 'Latest 5000' }} />
          <Btn small onClick={q.reload}>Refresh</Btn>
        </div>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          Only rows that actually carry a SendGrid message id — contact threads plus
          invitations, which is what the delivery webhooks can update.
        </p>
        {q.error && <Msg kind="error">{q.error}</Msg>}
        {q.loading ? <Loading /> : (
          <DataTable rows={d?.rows || []} searchable csvName="email_tracking"
            columns={['created_at', 'email_type', 'to_email', 'from_user', 'subject',
              'email_status', 'sendgrid_id', 'last_event']}
            empty="No tracked messages yet." />
        )}
      </Section>
      {(d?.by_status || []).length > 0 && (
        <Section title="Status distribution"><BarList items={d.by_status} /></Section>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
const Report: React.FC = () => {
  const [emailType, setEmailType] = useState('all');
  const [days, setDays] = useState('30');
  const report = useAdminGet('email', 'status_report',
    { email_type: emailType, days }, [emailType, days]);
  const d: any = report.data;

  const block = (title: string, obj: any) => obj && (
    <Section title={title}>
      <KPIRow>
        <KPI label="Total sent" value={obj.total_sent ?? 0} />
      </KPIRow>
      {obj.by_email_status && Object.keys(obj.by_email_status).length > 0 && (
        <BarList items={bars(obj.by_email_status)} />
      )}
      {obj.by_status && Object.keys(obj.by_status).length > 0 && (
        <BarList items={bars(obj.by_status)} />
      )}
    </Section>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2 items-center flex-wrap">
        <Select value={emailType} onChange={setEmailType}
          options={['all', 'invite', 'university_contact']}
          labels={{ all: 'All email types', invite: 'Invites', university_contact: 'University contacts' }} />
        <Select value={days} onChange={setDays} options={['7', '30', '90', '365']}
          labels={{ '7': 'Last 7 days', '30': 'Last 30 days', '90': 'Last 90 days', '365': 'Last year' }} />
        {d?.generated_at && <span className="text-xs text-grayscale-1100">Generated {fmtDate(d.generated_at)}</span>}
        <Btn small onClick={report.reload}>Refresh</Btn>
      </div>
      {report.error && <Msg kind="error">{report.error}</Msg>}
      {report.loading ? <Loading /> : d && (
        <div className="grid md:grid-cols-2 gap-4">
          {block('Invites', d.invites)}
          {block('University contacts', d.university_contacts)}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Campaign composer
// ---------------------------------------------------------------------------
type Tpl = {
  name: string; from_email: string; from_name: string; reply_to: string;
  bcc: string; type: string; subject: string; body: string;
};

const emptyTpl = (): Tpl => ({
  name: '', from_email: '', from_name: '', reply_to: '', bcc: '',
  type: 'Invited_Researcher', subject: '', body: '',
});

const asTpl = (t: any): Tpl => ({
  name: t?.name || '', from_email: t?.from_email || '', from_name: t?.from_name || '',
  reply_to: t?.reply_to || '', bcc: t?.bcc || '', type: t?.type || 'Invited_Researcher',
  subject: t?.subject || '', body: t?.body || '',
});

const TemplateFields: React.FC<{ tpl: Tpl; set: (patch: Partial<Tpl>) => void; withName?: boolean }> =
  ({ tpl, set, withName }) => (
  <>
    <div className="grid grid-cols-2 gap-3">
      {withName && <Field label="Template name" value={tpl.name} onChange={v => set({ name: v })} span2 />}
      <Field label="From email" value={tpl.from_email} onChange={v => set({ from_email: v })} />
      <Field label="From name" value={tpl.from_name} onChange={v => set({ from_name: v })} />
      <Field label="Reply-to" value={tpl.reply_to} onChange={v => set({ reply_to: v })} />
      <Field label="BCC" value={tpl.bcc} onChange={v => set({ bcc: v })} />
      <Field label="Campaign type (SendGrid custom arg)" value={tpl.type} onChange={v => set({ type: v })} />
      <Field label="Subject" value={tpl.subject} onChange={v => set({ subject: v })} span2 />
    </div>
    <Area label="Body" value={tpl.body} onChange={v => set({ body: v })} rows={14} />
  </>
);

const Composer: React.FC<{ cfg: any }> = ({ cfg }) => {
  const dbs = useAdminGet('email', 'campaign_db_status');
  const unis = useAdminGet('content', 'universities');
  const tpls = useAdminGet('email', 'campaign_templates');

  const [university, setUniversity] = useState('');
  const researchers = useAdminGet('email', 'campaign_researchers', { university }, [university]);

  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [onlyWithEmail, setOnlyWithEmail] = useState(true);
  const [hideEmailed, setHideEmailed] = useState(true);
  const [manual, setManual] = useState('');
  const [techSource, setTechSource] = useState('auto');
  const [useUtech, setUseUtech] = useState(true);
  const [pickedTechs, setPickedTechs] = useState<Record<string, any>>({});
  const [tpl, setTpl] = useState<Tpl>(emptyTpl());
  const [loaded, setLoaded] = useState('');
  const [useLinks, setUseLinks] = useState(false);
  const [tracked, setTracked] = useState(true);
  const [skipSent, setSkipSent] = useState(true);
  const [sendMode, setSendMode] = useState('individual');
  const [bitly, setBitly] = useState('');
  const [preview, setPreview] = useState<any>(null);
  const [result, setResult] = useState<any>(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState<Note>(null);
  const [techFor, setTechFor] = useState<any>(null);

  const uniTechs = useAdminGet('email', 'campaign_university_techs',
    { university: techSource === 'university' ? university : '',
      use_utech_title: useUtech ? 'true' : 'false', limit: 2000 },
    [university, techSource, useUtech]);

  // Pre-fill the composer from the built-in default the first time templates land.
  useEffect(() => {
    const all: any[] = [...((tpls.data as any)?.builtin || []), ...((tpls.data as any)?.saved || [])];
    if (!all.length || loaded) return;
    setTpl(asTpl(all[0]));
    setLoaded(all[0].name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tpls.data]);

  useEffect(() => { setPicked({}); setPreview(null); setResult(null); }, [university]);
  useEffect(() => { if (!bitly && cfg?.default_bitly) setBitly(cfg.default_bitly); }, [cfg, bitly]);

  const rows: any[] = (researchers.data as any)?.rows || [];
  const shown = useMemo(() => rows.filter(r =>
    (!onlyWithEmail || r.has_email) && (!hideEmailed || !r.already_emailed)),
    [rows, onlyWithEmail, hideEmailed]);
  const chosen = useMemo(() => shown.filter(r => picked[r.id]), [shown, picked]);
  const manualCount = manual.split(/[\n,;]+/).map(s => s.trim()).filter(s => s.includes('@')).length;
  const total = chosen.length + manualCount;
  const selectedTechs = useMemo(() => Object.values(pickedTechs), [pickedTechs]);

  const loadTemplate = (name: string) => {
    const all: any[] = [...((tpls.data as any)?.builtin || []), ...((tpls.data as any)?.saved || [])];
    const hit = all.find(t => t.name === name);
    setLoaded(name);
    if (hit) { setTpl(asTpl(hit)); setPreview(null); }
  };

  const sendBody = (confirm: boolean) => ({
    ...(confirm ? { confirm: true } : {}),
    template: tpl,
    recipients: chosen.map(r => ({ id: r.id, name: r.name, email: r.email, university: r.university })),
    manual_emails: manual,
    university,
    tech_source: techSource,
    selected_techs: techSource === 'university' ? selectedTechs : [],
    use_utech_title: useUtech,
    use_hyperlinks: useLinks,
    tracked,
    send_mode: sendMode,
    skip_already_sent: skipSent,
    bitly,
  });

  const runPreview = async () => {
    setBusy('preview'); setMsg(null);
    const { data, error } = await panelPost('email', 'campaign_preview', sendBody(false));
    setBusy('');
    if (error) { setPreview(null); setMsg({ kind: 'error', text: error }); }
    else setPreview(data);
  };

  const send = async () => {
    setBusy('send'); setMsg(null); setResult(null);
    const { data, error } = await panelPost('email', 'campaign_send', sendBody(true));
    setBusy(''); setConsent(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setResult(data);
    setMsg({
      kind: data.failed ? 'error' : 'ok',
      text: `Campaign ${data.campaign_id}: ${data.sent} sent, ${data.failed} failed, ${data.skipped} skipped (${data.send_mode}).`,
    });
    researchers.reload();
  };

  const db: any = dbs.data;

  return (
    <div className="flex flex-col gap-4">
      <Section title="Step 1 — university"
        right={<Btn small onClick={() => { unis.reload(); researchers.reload(); }}>Refresh</Btn>}>
        {db && (
          <div className="text-xs text-grayscale-1100">
            Technology catalogue: {db.exists ? `${db.size_mb} MB` : 'MISSING'} at {db.path}
            {db.exists && ` · ${(db.counts?.technologies ?? 0).toLocaleString()} technologies · ${(db.counts?.researchers ?? 0).toLocaleString()} researchers · ${(db.counts?.technology_researchers ?? 0).toLocaleString()} links`}
          </div>
        )}
        {!db?.exists && !dbs.loading && (
          <Msg kind="error">The technology catalogue is not on disk — the recipient picker will be empty.</Msg>
        )}
        {unis.error && <Msg kind="error">{unis.error}</Msg>}
        <div className="flex gap-2 items-center flex-wrap">
          <Select value={university} onChange={setUniversity}
            options={['', ...(unis.data || [])]} labels={{ '': 'Pick a university…' }} />
          {researchers.data && (
            <span className="text-xs text-grayscale-1100">
              {(researchers.data as any).total} researcher(s) · {(researchers.data as any).with_email} with an address ·
              {' '}{(researchers.data as any).already_emailed_count} already emailed
              {' '}(log holds {(researchers.data as any).already_emailed_total} addresses)
            </span>
          )}
        </div>
      </Section>

      <Section title={`Step 2 — recipients (${total} selected)`}
        right={<div className="flex gap-2">
          <Btn small onClick={() => setPicked(Object.fromEntries(shown.map(r => [r.id, true])))}>
            Select all shown
          </Btn>
          <Btn small onClick={() => setPicked({})}>Clear</Btn>
        </div>}>
        <div className="flex gap-4 flex-wrap">
          <Check checked={onlyWithEmail} onChange={setOnlyWithEmail}>Only researchers with an address</Check>
          <Check checked={hideEmailed} onChange={setHideEmailed}>Hide already-emailed</Check>
        </div>
        {researchers.error && <Msg kind="error">{researchers.error}</Msg>}
        {researchers.loading ? <Loading /> : (
          <DataTable rows={shown} searchable csvName="campaign_recipients" maxHeight="24rem"
            columns={['pick', 'name', 'email', 'university', 'country', 'contact_number', 'already_emailed']}
            labels={{ pick: '', already_emailed: 'Emailed before' }}
            empty={university ? 'No researchers match these filters.' : 'Pick a university first.'}
            render={{
              pick: r => (
                <input type="checkbox" className="accent-primary-500" checked={!!picked[r.id]}
                  onChange={e => setPicked(s => ({ ...s, [r.id]: e.target.checked }))} />
              ),
              already_emailed: r => r.already_emailed
                ? <span className="text-amber-700">already emailed</span>
                : <span className="text-grayscale-1100">—</span>,
            }}
            actions={r => <Btn small onClick={() => setTechFor(r)}>Technologies</Btn>} />
        )}
        <Area label="Extra recipients — one per line, 'Name <email>' or a bare address"
          value={manual} onChange={setManual} rows={4}
          placeholder={'Jane Doe <jane@uni.edu>\nsomeone@else.org'} />
        {cfg?.max_recipients && total > cfg.max_recipients && (
          <Msg kind="error">
            {total} recipients exceeds the {cfg.max_recipients}-per-campaign cap — the send will be refused.
          </Msg>
        )}
      </Section>

      <Section title="Step 3 — technologies in the email">
        <div className="flex gap-2 items-center flex-wrap">
          <Select value={techSource} onChange={setTechSource} options={['auto', 'university']}
            labels={{ auto: "Automatic — each recipient's own technologies",
              university: 'Choose from the university catalogue' }} />
          <Check checked={useUtech} onChange={setUseUtech}>Use UTech titles</Check>
        </div>
        {techSource === 'university' && (
          <>
            {uniTechs.error && <Msg kind="error">{uniTechs.error}</Msg>}
            {uniTechs.loading ? <Loading /> : (
              <DataTable rows={uniTechs.data || []} searchable maxHeight="18rem"
                columns={['pick', 'display_title', 'tech_id', 'url']}
                labels={{ pick: '', display_title: 'Technology' }}
                empty={university ? 'No technologies for this university.' : 'Pick a university first.'}
                render={{
                  pick: r => (
                    <input type="checkbox" className="accent-primary-500" checked={!!pickedTechs[r.tech_id]}
                      onChange={e => setPickedTechs(s => {
                        const next = { ...s };
                        if (e.target.checked) next[r.tech_id] = r; else delete next[r.tech_id];
                        return next;
                      })} />
                  ),
                }} />
            )}
            <div className="text-xs text-grayscale-1100">
              {selectedTechs.length} technology(ies) will be listed in every email.
            </div>
          </>
        )}
      </Section>

      <Section title="Step 4 — template"
        right={<Select value={loaded} onChange={loadTemplate}
          options={['', ...((tpls.data as any)?.loader_names || [])]}
          labels={{ '': 'Load a template…' }} />}>
        {tpls.error && <Msg kind="error">{tpls.error}</Msg>}
        <TemplateFields tpl={tpl} set={p => setTpl(s => ({ ...s, ...p }))} />
        <div className="text-xs text-grayscale-1100">
          Placeholders: {((tpls.data as any)?.placeholders || cfg?.placeholders || []).join('  ')}
        </div>
        <div className="flex gap-4 flex-wrap items-center">
          <TextInput value={bitly} onChange={setBitly} placeholder="{Bitly Link} target" wide />
          <Check checked={useLinks} onChange={setUseLinks}>Send an HTML part with hyperlinks</Check>
          <Check checked={tracked} onChange={setTracked}>Open / click tracking</Check>
          <Check checked={skipSent} onChange={setSkipSent}>Skip addresses already in the sent log</Check>
          <Select value={sendMode} onChange={setSendMode} options={['individual', 'single']}
            labels={{ individual: 'One personalised email each', single: 'One email, everyone in TO' }} />
        </div>
      </Section>

      <Section title="Step 5 — preview & send"
        right={<Btn small kind="primary" disabled={busy === 'preview'} onClick={runPreview}>
          {busy === 'preview' ? 'Rendering…' : 'Preview first recipient'}
        </Btn>}>
        {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
        {preview && (preview.has_recipient ? (
          <div className="flex flex-col gap-2">
            <div className="text-xs text-grayscale-1100">
              {preview.recipient_count} resolved recipient(s) · From {preview.from}
              {preview.reply_to ? ` · Reply-to ${preview.reply_to}` : ''}
              {preview.bcc ? ` · BCC ${preview.bcc}` : ''}
            </div>
            <div className="text-sm text-grayscale-1300"><b>To:</b> {preview.to}</div>
            <div className="text-sm text-grayscale-1300"><b>Subject:</b> {preview.subject}</div>
            <Pre>{preview.body || ''}</Pre>
            {(preview.technologies || []).length > 0 && (
              <div className="text-xs text-grayscale-1100">
                Technologies listed: {preview.technologies.map((t: any) => t.title).join(' · ')}
              </div>
            )}
          </div>
        ) : <Msg kind="error">No resolvable recipient — nothing would be sent.</Msg>)}

        <SendGate
          label={`Send to ${total} recipient(s)`}
          consent={`I have previewed this and want to email ${total} recipient(s) now.`}
          checked={consent} onChange={setConsent} busy={busy === 'send'} onSend={send}
          blocked={!total ? 'Select at least one recipient.'
            : !tpl.subject.trim() ? 'The subject is empty.'
              : !cfg?.sendgrid_configured ? 'SendGrid is not configured on the server.'
                : undefined} />

        {result && (
          <div className="flex flex-col gap-2">
            <KPIRow>
              <KPI label="Sent" value={result.sent} />
              <KPI label="Failed" value={result.failed} />
              <KPI label="Skipped" value={result.skipped} hint="already emailed / no address" />
            </KPIRow>
            <DataTable rows={result.results || []} csvName="campaign_result" maxHeight="18rem"
              columns={['email', 'status', 'reason', 'message_id', 'error']}
              empty="No per-recipient results returned." />
          </div>
        )}
      </Section>

      {techFor && <ResearcherTechs researcher={techFor} useUtech={useUtech}
        onClose={() => setTechFor(null)} />}
    </div>
  );
};

const ResearcherTechs: React.FC<{ researcher: any; useUtech: boolean; onClose: () => void }> =
  ({ researcher, useUtech, onClose }) => {
  const q = useAdminGet('email', 'campaign_researcher_techs',
    { researcher_id: researcher.id, use_utech_title: useUtech ? 'true' : 'false', limit: 200 },
    [researcher.id, useUtech]);
  return (
    <Drawer title={researcher.name || researcher.email || 'Researcher'} onClose={onClose}
      subtitle={`${researcher.email || 'no address'} · ${researcher.university || '—'}`}>
      {q.error && <Msg kind="error">{q.error}</Msg>}
      <Section title="Technologies this researcher is attached to">
        {q.loading ? <Loading /> : (
          <DataTable rows={q.data || []} csvName="researcher_technologies"
            columns={['display_title', 'tech_id', 'university', 'url']}
            labels={{ display_title: 'Title as it will appear' }}
            empty="No linked technologies — the email will say so." />
        )}
      </Section>
    </Drawer>
  );
};

// ---------------------------------------------------------------------------
// Sent log + statistics + delivery refresh + danger zone
// ---------------------------------------------------------------------------
const LOG_PAGE = 200;

const SentLog: React.FC = () => {
  const [status, setStatus] = useState('All');
  const [university, setUniversity] = useState('');
  const [email, setEmail] = useState('');
  const [mode, setMode] = useState('All');
  const [campaign, setCampaign] = useState('All');
  const [page, setPage] = useState(0);
  const [msg, setMsg] = useState<Note>(null);
  const [useApi, setUseApi] = useState(false);
  const [busy, setBusy] = useState('');
  const [clearWord, setClearWord] = useState('');
  const [clearScope, setClearScope] = useState('All');

  const log = useAdminGet('email', 'campaign_log', {
    status: status === 'All' ? '' : status,
    university, email,
    send_mode: mode === 'All' ? '' : mode,
    campaign_id: campaign === 'All' ? '' : campaign,
    limit: LOG_PAGE, offset: page * LOG_PAGE,
  }, [status, university, email, mode, campaign, page]);
  const stats = useAdminGet('email', 'campaign_stats');

  const d: any = log.data;
  const total = d?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / LOG_PAGE));

  const refresh = async () => {
    setBusy('refresh'); setMsg(null);
    const { data, error } = await panelPost('email', 'campaign_refresh_status',
      { use_api: useApi, api_limit: 300 });
    setBusy('');
    if (error) setMsg({ kind: 'error', text: error });
    else {
      setMsg({ kind: data.updated ? 'ok' : 'error', text: `${data.message} (store ${data.from_store}, API ${data.from_api}, ${data.store_rows} SendGrid rows available)` });
      log.reload();
    }
  };

  const clearLog = async () => {
    setBusy('clear'); setMsg(null);
    const { data, error } = await panelPost('email', 'campaign_clear_log',
      { confirm: clearWord.trim(), campaign_id: clearScope === 'All' ? '' : clearScope });
    setBusy(''); setClearWord('');
    if (error) setMsg({ kind: 'error', text: error });
    else {
      setMsg({ kind: 'ok', text: `Deleted ${data.deleted} log row(s) (scope ${data.scope}).` });
      log.reload(); stats.reload();
    }
  };

  const st: any = stats.data;
  const statCols = ['university', 'total', ...((st?.statuses || []) as string[])];

  return (
    <div className="flex flex-col gap-4">
      {d && (
        <KPIRow>
          <KPI label="Log rows" value={(d.kpi?.all ?? 0).toLocaleString()} hint="whole table, not the filter" />
          <KPI label="Sent" value={d.kpi?.sent ?? 0} />
          <KPI label="Delivered" value={d.kpi?.delivered ?? 0} />
          <KPI label="Opened" value={d.kpi?.opened ?? 0} />
          <KPI label="Clicked" value={d.kpi?.clicked ?? 0} />
          <KPI label="Bounced" value={d.kpi?.bounced ?? 0} />
          <KPI label="Failed" value={d.kpi?.failed ?? 0} />
        </KPIRow>
      )}
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}

      <Section title={`Campaign sent log (${total.toLocaleString()} matching rows)`}
        right={<div className="flex gap-2 items-center">
          <Check checked={useApi} onChange={setUseApi}>Also query the SendGrid Activity API</Check>
          <Btn small kind="primary" disabled={busy === 'refresh'} onClick={refresh}>
            {busy === 'refresh' ? 'Refreshing…' : 'Refresh delivery status'}
          </Btn>
          <Btn small onClick={log.reload}>Reload</Btn>
        </div>}>
        <div className="flex gap-2 flex-wrap items-center">
          <Select value={status} onChange={v => { setStatus(v); setPage(0); }}
            options={['All', ...((d?.statuses || []) as string[])]} />
          <Select value={mode} onChange={v => { setMode(v); setPage(0); }}
            options={['All', 'individual', 'bulk_single', 'bulk', 'manual', 'bulk,bulk_single',
              'invite_backfill', 'bulk_add_invite', 'invite_backfill,bulk_add_invite']}
            labels={{ 'bulk,bulk_single': 'Any bulk mode', invite_backfill: 'Invites (Users → Send invites)',
              bulk_add_invite: 'Invites (Users → Bulk add)', 'invite_backfill,bulk_add_invite': 'Any invite' }} />
          <Select value={campaign} onChange={v => { setCampaign(v); setPage(0); }}
            options={['All', ...((d?.campaigns || []) as any[]).map(c => c.campaign_id)]} />
          <TextInput value={university} onChange={v => { setUniversity(v); setPage(0); }}
            placeholder="University contains…" />
          <TextInput value={email} onChange={v => { setEmail(v); setPage(0); }}
            placeholder="Address contains…" />
        </div>
        {log.error && <Msg kind="error">{log.error}</Msg>}
        {log.loading ? <Loading /> : (
          <DataTable rows={d?.rows || []} csvName="campaign_sent_log"
            columns={['sent_at', 'campaign_id', 'researcher_name', 'researcher_email', 'university',
              'subject', 'status', 'status_code', 'send_mode', 'template_type', 'technologies',
              'sendgrid_message_id', 'last_event_time', 'sent_by', 'error']}
            empty="Nothing has been sent from this panel yet." />
        )}
        <div className="flex items-center gap-2 justify-end text-xs text-grayscale-1100">
          <Btn small disabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</Btn>
          Page {page + 1} of {pages}
          <Btn small disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)}>Next</Btn>
        </div>
      </Section>

      {d && (
        <div className="grid md:grid-cols-2 gap-4">
          <Section title="By status">
            {Object.keys(d.status_counts || {}).length
              ? <BarList items={bars(d.status_counts)} />
              : <div className="text-sm text-grayscale-1100">No rows logged yet.</div>}
          </Section>
          <Section title="By send mode">
            {Object.keys(d.mode_counts || {}).length
              ? <BarList items={bars(d.mode_counts)} />
              : <div className="text-sm text-grayscale-1100">No rows logged yet.</div>}
          </Section>
        </div>
      )}

      <Section title="Campaign statistics" right={<Btn small onClick={stats.reload}>Refresh</Btn>}>
        {stats.error && <Msg kind="error">{stats.error}</Msg>}
        {stats.loading ? <Loading /> : (
          <>
            <KPIRow>
              <KPI label="Campaigns" value={st?.campaigns ?? 0} />
              <KPI label="Researchers reached" value={st?.unique_researchers_sent ?? 0}
                hint="unique addresses with a successful send" />
              <KPI label="Researchers attempted" value={st?.unique_researchers_any ?? 0} />
            </KPIRow>
            <DataTable rows={st?.by_university || []} csvName="campaign_stats_by_university"
              columns={statCols} empty="No campaign has been sent yet." />
          </>
        )}
      </Section>

      <Section title="Danger zone — clear the sent log">
        <p className="text-xs text-grayscale-1100 -mt-1">
          The sent log is also the already-emailed suppression list. Clearing it re-enables
          sending to every researcher who has already been contacted. Type <b>CLEAR</b> to arm.
        </p>
        <div className="flex gap-2 items-center flex-wrap">
          <Select value={clearScope} onChange={setClearScope}
            options={['All', ...((d?.campaigns || []) as any[]).map(c => c.campaign_id)]}
            labels={{ All: 'Every campaign' }} />
          <TextInput value={clearWord} onChange={setClearWord} placeholder="Type CLEAR" />
          {clearWord.trim().toUpperCase() === 'CLEAR' && busy !== 'clear'
            ? <DangerBtn small onConfirm={clearLog}>
                Delete {clearScope === 'All' ? 'the whole log' : clearScope}
              </DangerBtn>
            : <Btn small kind="danger" disabled>
                Delete {clearScope === 'All' ? 'the whole log' : clearScope}
              </Btn>}
        </div>
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Campaign template manager
// ---------------------------------------------------------------------------
const TemplateManager: React.FC = () => {
  const q = useAdminGet('email', 'campaign_templates');
  const [tpl, setTpl] = useState<Tpl>(emptyTpl());
  const [msg, setMsg] = useState<Note>(null);
  const [busy, setBusy] = useState(false);
  const d: any = q.data;

  const all = useMemo(() => [
    ...((d?.builtin || []) as any[]),
    ...((d?.saved || []) as any[]),
  ], [d]);

  const save = async () => {
    setBusy(true); setMsg(null);
    const { data, error } = await panelPost('email', 'template_save', tpl);
    setBusy(false);
    if (error) setMsg({ kind: 'error', text: error });
    else { setMsg({ kind: 'ok', text: `Saved template "${data.name}".` }); q.reload(); }
  };

  const remove = async (name: string) => {
    const { error } = await panelPost('email', 'template_delete', { name });
    if (error) setMsg({ kind: 'error', text: error });
    else { setMsg({ kind: 'ok', text: `Deleted "${name}".` }); q.reload(); }
  };

  const importFile = async (file: File) => {
    setMsg(null);
    const text = await file.text();
    const { data, error } = await panelPost('email', 'templates_import', { templates: text });
    if (error) setMsg({ kind: 'error', text: error });
    else {
      setMsg({
        kind: data.count ? 'ok' : 'error',
        text: `Imported ${data.count}: ${(data.imported || []).join(', ') || 'none'}.`
          + ((data.skipped || []).length ? ` Skipped: ${data.skipped.join(', ')}.` : ''),
      });
      q.reload();
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      {q.error && <Msg kind="error">{q.error}</Msg>}
      <Section title="Campaign templates"
        right={<div className="flex gap-2 items-center">
          <Btn small disabled={!d} onClick={() => downloadJson('campaign_templates', d?.export || {})}>
            Export saved
          </Btn>
          <label className="text-xs text-grayscale-1100 flex items-center gap-1 cursor-pointer">
            Import JSON
            <input type="file" accept=".json,application/json" className="text-xs w-40"
              onChange={e => e.target.files?.[0] && importFile(e.target.files[0])} />
          </label>
          <Btn small onClick={q.reload}>Refresh</Btn>
        </div>}>
        {q.loading ? <Loading /> : (
          <DataTable rows={all} csvName="campaign_templates"
            columns={['name', 'type', 'subject', 'from_email', 'reply_to', 'bcc', 'updated_at', 'updated_by']}
            empty="No templates — the built-ins should always be here, so this means the read failed."
            actions={r => (
              <div className="flex gap-1">
                <Btn small onClick={() => { setTpl(asTpl(r)); setMsg(null); }}>Load into editor</Btn>
                {r.deletable
                  ? <DangerBtn small onConfirm={() => remove(r.name)}>Delete</DangerBtn>
                  : <span className="text-xs text-grayscale-1100">built-in</span>}
              </div>
            )} />
        )}
      </Section>
      <Section title="Editor"
        right={<Btn small kind="primary" disabled={busy || !tpl.name.trim()} onClick={save}>
          {busy ? 'Saving…' : 'Save template'}
        </Btn>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          Saving upserts by name. Built-in names are refused by the server — give a copy its own name.
          Placeholders: {((d?.placeholders || []) as string[]).join('  ')}
        </p>
        <TemplateFields tpl={tpl} set={p => setTpl(s => ({ ...s, ...p }))} withName />
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Manual single send
// ---------------------------------------------------------------------------
const ManualSend: React.FC<{ cfg: any }> = ({ cfg }) => {
  const tpls = useAdminGet('email', 'campaign_templates');
  const [to, setTo] = useState('');
  const [tpl, setTpl] = useState<Tpl>({ ...emptyTpl(), type: 'Manual' });
  const [loaded, setLoaded] = useState('');
  const [link, setLink] = useState('');
  const [useLinks, setUseLinks] = useState(true);
  const [tracked, setTracked] = useState(true);
  const [personalize, setPersonalize] = useState(true);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Note>(null);
  const [result, setResult] = useState<any>(null);

  useEffect(() => { if (!link && cfg?.default_bitly) setLink(cfg.default_bitly); }, [cfg, link]);

  const count = to.split(/[\n,;]+/).map(s => s.trim()).filter(s => s.includes('@')).length;

  const loadTemplate = (name: string) => {
    const all: any[] = [...((tpls.data as any)?.builtin || []), ...((tpls.data as any)?.saved || [])];
    const hit = all.find(t => t.name === name);
    setLoaded(name);
    if (hit) setTpl(asTpl(hit));
  };

  const send = async () => {
    setBusy(true); setMsg(null); setResult(null);
    const { data, error } = await panelPost('email', 'send_manual', {
      confirm: true, to, subject: tpl.subject, body: tpl.body,
      from_email: tpl.from_email, from_name: tpl.from_name, reply_to: tpl.reply_to,
      bcc: tpl.bcc, type: tpl.type, default_link: link,
      use_hyperlinks: useLinks, tracked, personalize,
    });
    setBusy(false); setConsent(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setResult(data);
    setMsg({
      kind: data.failed ? 'error' : 'ok',
      text: `${data.campaign_id}: ${data.sent} sent, ${data.failed} failed across ${data.recipients} recipient(s)`
        + (data.personalized ? ' (personalised individually).' : ' (one shared message).'),
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <Section title="Send a template manually"
        right={<Select value={loaded} onChange={loadTemplate}
          options={['', ...((tpls.data as any)?.loader_names || [])]}
          labels={{ '': 'Load a template…' }} />}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          Personalised sends mail one message per recipient, so nobody is greeted by someone
          else's name. Every send lands in the campaign sent log and therefore in the
          already-emailed suppression list. Placeholders: {'{Name}'} {'{first_name}'} {'{Bitly Link}'} {'{professional_summary}'}
        </p>
        {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
        <Area label="Recipients — one per line, 'Name <email>' or a bare address"
          value={to} onChange={setTo} rows={5}
          placeholder={'Jane Doe <jane@uni.edu>\nsomeone@else.org'} />
        <TemplateFields tpl={tpl} set={p => setTpl(s => ({ ...s, ...p }))} />
        <div className="flex gap-4 flex-wrap items-center">
          <TextInput value={link} onChange={setLink} placeholder="{Bitly Link} target" wide />
          <Check checked={personalize} onChange={setPersonalize}>Personalise per recipient</Check>
          <Check checked={useLinks} onChange={setUseLinks}>HTML part with hyperlinks</Check>
          <Check checked={tracked} onChange={setTracked}>Open / click tracking</Check>
        </div>
        <SendGate
          label={`Send to ${count} recipient(s)`}
          consent={`I want to email ${count} recipient(s) now.`}
          checked={consent} onChange={setConsent} busy={busy} onSend={send}
          blocked={!count ? 'Add at least one recipient address.'
            : !tpl.subject.trim() ? 'The subject is required.'
              : !cfg?.sendgrid_configured ? 'SendGrid is not configured on the server.'
                : undefined} />
        {result && (
          <DataTable rows={result.results || []} csvName="manual_send_result" maxHeight="18rem"
            columns={['email', 'status', 'message_id', 'error']}
            empty="No per-recipient results returned." />
        )}
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Bulk mail-merge from CSV / XLSX
// ---------------------------------------------------------------------------
type MapOpt = {
  placeholder: string; max_len: string; transform: string;
  split_delim: string; split_idx: string;
};

const BulkMerge: React.FC<{ cfg: any }> = ({ cfg }) => {
  const batches = useAdminGet('email', 'bulk_batches', { limit: 20 });
  const [upload, setUpload] = useState<any>(null);
  const [mapping, setMapping] = useState<Record<string, MapOpt>>({});
  const [toColumn, setToColumn] = useState('');
  const [manualTo, setManualTo] = useState('');
  const [rowSpec, setRowSpec] = useState('');
  const [tpl, setTpl] = useState<Tpl>({ ...emptyTpl(), type: 'Bulk' });
  const [link, setLink] = useState('');
  const [useLinks, setUseLinks] = useState(false);
  const [tracked, setTracked] = useState(true);
  const [delay, setDelay] = useState('2');
  const [attachment, setAttachment] = useState<any>(null);
  const [preview, setPreview] = useState<any>(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState<Note>(null);
  const [watchId, setWatchId] = useState('');
  const [status, setStatus] = useState<any>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => { if (!link && cfg?.default_bitly) setLink(cfg.default_bitly); }, [cfg, link]);

  useEffect(() => {
    if (!upload) return;
    const init: Record<string, MapOpt> = {};
    (upload.columns || []).forEach((c: string) => {
      init[c] = { placeholder: '', max_len: '0', transform: 'None', split_delim: ' ', split_idx: '0' };
    });
    setMapping(init);
    setToColumn(upload.suggested_to_column || (upload.columns || [])[0] || '');
    setPreview(null);
  }, [upload]);

  // Progress poll — stops as soon as the daemon thread reports done/error.
  useEffect(() => {
    if (!watchId) { setStatus(null); return; }
    let alive = true;
    let timer: any;
    const tick = async () => {
      const { data, error } = await panelGet('email', 'bulk_status', { upload_id: watchId, limit: 200 });
      if (!alive) return;
      if (error) { setMsg({ kind: 'error', text: error }); return; }
      setStatus(data);
      if (data?.progress?.state === 'running') timer = setTimeout(tick, 3000);
    };
    tick();
    return () => { alive = false; clearTimeout(timer); };
  }, [watchId, nonce]);

  const rowCount = upload?.row_count ?? 0;
  const indices = useMemo(() => parseRanges(rowSpec, rowCount), [rowSpec, rowCount]);
  const selectedCount = indices.length || rowCount;

  const mappingBody = () => {
    const out: Record<string, any> = {};
    Object.entries(mapping).forEach(([col, o]) => {
      if (!o.placeholder.trim() && (o.transform || 'None') === 'None') return;
      out[col] = {
        placeholder: o.placeholder.trim(),
        max_len: Number(o.max_len) || 0,
        transform: o.transform || 'None',
        split_delim: o.split_delim,
        split_idx: Number(o.split_idx) || 0,
      };
    });
    return out;
  };

  const onFile = async (file: File) => {
    setBusy('upload'); setMsg(null);
    const { data, error } = await panelPost('email', 'bulk_upload',
      { filename: file.name, content_b64: await readB64(file) });
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setUpload(data); setAttachment(null); setWatchId('');
    setMsg({ kind: 'ok', text: `${data.row_count} row(s), ${data.columns.length} column(s) staged as ${data.upload_id}.` });
    batches.reload();
  };

  const onAttach = async (file: File | null) => {
    if (!upload) return;
    setBusy('attach'); setMsg(null);
    const body: Record<string, any> = { upload_id: upload.upload_id, filename: file?.name || '' };
    if (file) { body.content_b64 = await readB64(file); body.mime = file.type; }
    const { data, error } = await panelPost('email', 'bulk_attachment', body);
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setAttachment(data.attached ? data : null);
    setMsg({ kind: 'ok', text: data.attached ? `Attached ${data.filename} (${data.bytes} bytes).` : 'Attachment cleared.' });
  };

  const runPreview = async () => {
    if (!upload) return;
    setBusy('preview'); setMsg(null);
    const { data, error } = await panelPost('email', 'bulk_preview', {
      upload_id: upload.upload_id, mapping: mappingBody(), to_column: toColumn,
      manual_to: manualTo, row_indices: indices, limit: 5, use_hyperlinks: useLinks,
      template: { subject: tpl.subject, body: tpl.body, default_link: link },
    });
    setBusy('');
    if (error) { setPreview(null); setMsg({ kind: 'error', text: error }); }
    else setPreview(data);
  };

  const send = async () => {
    if (!upload) return;
    setBusy('send'); setMsg(null);
    const { data, error } = await panelPost('email', 'bulk_send', {
      confirm: true, upload_id: upload.upload_id, mapping: mappingBody(),
      to_column: toColumn, manual_to: manualTo, row_indices: indices,
      template: { ...tpl, default_link: link }, tracked, use_hyperlinks: useLinks,
      delay_sec: Number(delay) || 0,
    });
    setBusy(''); setConsent(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setMsg({ kind: 'ok', text: `Batch ${data.batch} started for ${data.total} row(s); ETA ${data.eta_seconds}s at ${data.delay_sec}s between sends.` });
    setWatchId(upload.upload_id); setNonce(n => n + 1);
    batches.reload();
  };

  const prog = status?.progress || {};

  return (
    <div className="flex flex-col gap-4">
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}

      <Section title="Step 1 — upload a CSV or XLSX"
        right={<Btn small onClick={batches.reload}>Refresh batches</Btn>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          The file is parsed server-side so the preview and the sender read exactly the same
          rows. Batches are staged for 7 days
          {cfg?.max_bulk_rows ? `, up to ${cfg.max_bulk_rows.toLocaleString()} rows each` : ''}.
        </p>
        <input type="file" accept=".csv,.xlsx" className="text-sm"
          onChange={e => e.target.files?.[0] && onFile(e.target.files[0])} />
        {busy === 'upload' && <Loading label="Parsing…" />}
        {upload && (
          <>
            <div className="text-xs text-grayscale-1100">
              {upload.filename} · {upload.row_count} rows · columns: {(upload.columns || []).join(', ')}
            </div>
            <DataTable rows={upload.preview || []} maxHeight="16rem"
              empty="The file parsed but has no data rows." />
          </>
        )}
        {batches.error && <Msg kind="error">{batches.error}</Msg>}
        <div className="border-t border-grayscale-300 pt-3">
          <div className="text-xs text-grayscale-1100 mb-2">
            Recent batches — reattach to one to watch a send that started before this page loaded.
          </div>
          {batches.loading ? <Loading /> : (
            <DataTable rows={batches.data || []} maxHeight="14rem"
              columns={['created_at', 'filename', 'row_count', 'column_count', 'attachment_name', 'admin_email']}
              empty="No staged batches in the last 7 days."
              actions={r => (
                <Btn small onClick={() => { setWatchId(r.id); setNonce(n => n + 1); }}>Watch progress</Btn>
              )} />
          )}
        </div>
      </Section>

      {upload && (
        <>
          <Section title="Step 2 — map columns to placeholders">
            <p className="text-xs text-grayscale-1100 -mt-1">
              At least one column must carry a placeholder or the send is refused. A placeholder
              named <b>Name</b> also supplies the greeting fallback.
            </p>
            <div className="overflow-auto rounded-lg border border-grayscale-300" style={{ maxHeight: '24rem' }}>
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-grayscale-200">
                  <tr>
                    {['Column', 'Placeholder', 'Transform', 'Delimiter', 'Index', 'Max length'].map(h => (
                      <th key={h} className="text-left font-grotesk text-xs text-grayscale-1200 px-3 py-2">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(upload.columns || []).map((col: string) => {
                    const o = mapping[col] || { placeholder: '', max_len: '0', transform: 'None', split_delim: ' ', split_idx: '0' };
                    const set = (patch: Partial<MapOpt>) =>
                      setMapping(s => ({ ...s, [col]: { ...o, ...patch } }));
                    return (
                      <tr key={col} className="border-t border-grayscale-200">
                        <td className="px-3 py-1.5 text-grayscale-1300 whitespace-nowrap">{col}</td>
                        <td className="px-3 py-1.5">
                          <input value={o.placeholder} placeholder="e.g. Name"
                            onChange={e => set({ placeholder: e.target.value })}
                            className="h-8 px-2 rounded border-[0.5px] border-primary-300 text-sm w-36" />
                        </td>
                        <td className="px-3 py-1.5">
                          <Select value={o.transform} onChange={v => set({ transform: v })}
                            options={['None', 'first_word', 'split']}
                            labels={{ first_word: 'First word only', split: 'Split on delimiter' }} />
                        </td>
                        <td className="px-3 py-1.5">
                          <input value={o.split_delim} disabled={o.transform !== 'split'}
                            onChange={e => set({ split_delim: e.target.value })}
                            className="h-8 px-2 rounded border-[0.5px] border-primary-300 text-sm w-16 disabled:opacity-40" />
                        </td>
                        <td className="px-3 py-1.5">
                          <input type="number" value={o.split_idx} disabled={o.transform !== 'split'}
                            onChange={e => set({ split_idx: e.target.value })}
                            className="h-8 px-2 rounded border-[0.5px] border-primary-300 text-sm w-16 disabled:opacity-40" />
                        </td>
                        <td className="px-3 py-1.5">
                          <input type="number" value={o.max_len}
                            onChange={e => set({ max_len: e.target.value })}
                            className="h-8 px-2 rounded border-[0.5px] border-primary-300 text-sm w-20" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex gap-2 flex-wrap items-end">
              <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
                To-email column
                <Select value={toColumn} onChange={setToColumn} options={upload.columns || []} />
              </label>
              <Field label="Manual override — send every row to this one address instead"
                value={manualTo} onChange={setManualTo} placeholder="you@universitytech.io" span2 />
            </div>
            <Field label={`Rows to send (1-based, e.g. "1-50, 73"). Empty means all ${rowCount}.`}
              value={rowSpec} onChange={setRowSpec} placeholder={`1-${rowCount}`} span2 />
          </Section>

          <Section title="Step 3 — message & attachment">
            <TemplateFields tpl={tpl} set={p => setTpl(s => ({ ...s, ...p }))} />
            <div className="flex gap-4 flex-wrap items-center">
              <TextInput value={link} onChange={setLink} placeholder="{Bitly Link} target" wide />
              <Check checked={useLinks} onChange={setUseLinks}>HTML part with hyperlinks</Check>
              <Check checked={tracked} onChange={setTracked}>Open / click tracking</Check>
              <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
                Seconds between sends (0–60)
                <input type="number" value={delay} min={0} max={60}
                  onChange={e => setDelay(e.target.value)}
                  className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm w-24" />
              </label>
            </div>
            <div className="flex gap-2 items-center flex-wrap border-t border-grayscale-300 pt-3">
              <span className="text-xs text-grayscale-1100">
                One optional file rides on every message
                {cfg?.attachment_extensions ? ` (${cfg.attachment_extensions.join(' ')})` : ''}:
              </span>
              <input type="file" className="text-sm"
                onChange={e => e.target.files?.[0] && onAttach(e.target.files[0])} />
              {attachment && (
                <>
                  <span className="text-xs text-grayscale-1300">{attachment.filename} ({attachment.bytes} bytes)</span>
                  <Btn small onClick={() => onAttach(null)}>Clear</Btn>
                </>
              )}
              {busy === 'attach' && <span className="text-xs text-grayscale-1100">Uploading…</span>}
            </div>
          </Section>

          <Section title="Step 4 — preview & send"
            right={<Btn small kind="primary" disabled={busy === 'preview'} onClick={runPreview}>
              {busy === 'preview' ? 'Rendering…' : 'Preview'}
            </Btn>}>
            {preview && (
              <div className="flex flex-col gap-3">
                <div className="text-xs text-grayscale-1100">
                  {preview.total_selected} row(s) selected · placeholders {(preview.placeholders || []).join(' ') || 'none'}
                  {preview.invalid_recipients ? ` · ${preview.invalid_recipients} row(s) have no usable address` : ''}
                </div>
                {!preview.can_send && (
                  <Msg kind="error">
                    Not sendable yet — map at least one placeholder and choose a To column
                    (or a manual override).
                  </Msg>
                )}
                {Object.keys(preview.column_previews || {}).length > 0 && (
                  <div className="flex flex-col gap-2">
                    {Object.entries(preview.column_previews).map(([col, samples]) => (
                      <div key={col} className="text-xs text-grayscale-1100">
                        <b className="text-grayscale-1300">{col}</b>:{' '}
                        {(samples as any[]).map((s, i) => (
                          <span key={i}>{s.original || '∅'} → <b className="text-grayscale-1300">{s.result || '∅'}</b>{i < (samples as any[]).length - 1 ? ' · ' : ''}</span>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
                {(preview.rows || []).map((r: any) => (
                  <div key={r.index} className="border border-grayscale-300 rounded-xl p-3 bg-white flex flex-col gap-1">
                    <div className="text-xs text-grayscale-1100">
                      Row {r.row} · To <b className={r.valid ? 'text-grayscale-1300' : 'text-red-600'}>{r.to || '(no address)'}</b>
                    </div>
                    <div className="text-sm text-grayscale-1300"><b>Subject:</b> {r.subject}</div>
                    <Pre>{r.body || ''}</Pre>
                  </div>
                ))}
              </div>
            )}
            <SendGate
              label={`Send ${selectedCount} message(s)`}
              consent={`I have previewed this merge and want to send ${selectedCount} message(s).`}
              checked={consent} onChange={setConsent} busy={busy === 'send'} onSend={send}
              blocked={!preview?.can_send ? 'Run a preview that reports the batch as sendable first.'
                : !tpl.subject.trim() ? 'The subject is empty.'
                  : !cfg?.sendgrid_configured ? 'SendGrid is not configured on the server.'
                    : undefined} />
          </Section>
        </>
      )}

      {watchId && (
        <Section title="Send progress"
          right={<div className="flex gap-2 items-center">
            <span className="text-xs text-grayscale-1100">{watchId}</span>
            <Btn small onClick={() => setNonce(n => n + 1)}>Refresh</Btn>
            <Btn small onClick={() => setWatchId('')}>Stop watching</Btn>
          </div>}>
          {status?.found === false
            ? <Msg kind="error">That batch has expired — staged batches are kept 7 days.</Msg>
            : (
              <>
                <KPIRow>
                  <KPI label="State" value={prog.state || 'idle'} />
                  <KPI label="Done" value={`${prog.done ?? 0} / ${prog.total ?? status?.row_count ?? 0}`}
                    hint={`${prog.percent ?? 0}%`} />
                  <KPI label="Sent" value={prog.sent ?? 0} />
                  <KPI label="Failed" value={prog.failed ?? 0} />
                  <KPI label="Started" value={prog.started_at ? fmtDate(prog.started_at) : '—'} />
                  <KPI label="Finished" value={prog.finished_at ? fmtDate(prog.finished_at) : '—'} />
                </KPIRow>
                {prog.error && <Msg kind="error">{prog.error}</Msg>}
                {(prog.errors || []).length > 0 && (
                  <DataTable rows={prog.errors} maxHeight="12rem" columns={['row', 'to', 'error']} />
                )}
                <DataTable rows={status?.recent || []} csvName="bulk_send_log" maxHeight="18rem"
                  columns={['sent_at', 'researcher_email', 'researcher_name', 'subject', 'status',
                    'status_code', 'sendgrid_message_id', 'error']}
                  empty="Nothing written to the log for this batch yet." />
              </>
            )}
        </Section>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// SendGrid Email Logs
// ---------------------------------------------------------------------------
const SendGridLogs: React.FC = () => {
  const [exclude, setExclude] = useState('info@universitytech.io');
  const [status, setStatus] = useState('All');
  const [subject, setSubject] = useState('');
  const [recipient, setRecipient] = useState('');
  const [sender, setSender] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [limit, setLimit] = useState('1000');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState<Note>(null);

  const q = useAdminGet('email', 'sendgrid_logs', {
    exclude_recipients: exclude, status: status === 'All' ? '' : status,
    subject, recipient, sender, start_date: start, end_date: end, limit,
  }, [exclude, status, subject, recipient, sender, start, end, limit]);
  const d: any = q.data;
  const m = d?.metrics || {};

  const pull = async () => {
    setBusy('pull'); setMsg(null);
    const { data, error } = await panelPost('email', 'sendgrid_pull',
      { start_date: start, end_date: end });
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    if (data.success) {
      setMsg({ kind: 'ok', text: `Pulled ${data.pulled} message(s) for ${(data.range || []).join(' → ')}; store now holds ${data.total}.` });
    } else if (data.fallback) {
      setMsg({ kind: 'error', text: `${data.error} Fell back to the local webhook table: loaded ${data.local?.loaded ?? 0}.` });
    } else {
      setMsg({ kind: 'error', text: data.error || 'Pull failed' });
    }
    q.reload();
  };

  const loadLocal = async () => {
    setBusy('local'); setMsg(null);
    const { data, error } = await panelPost('email', 'sendgrid_load_local', { limit: 5000 });
    setBusy('');
    if (error) setMsg({ kind: 'error', text: error });
    else { setMsg({ kind: 'ok', text: `Rebuilt ${data.loaded} row(s) from our own email_events; store holds ${data.total}.` }); q.reload(); }
  };

  return (
    <div className="flex flex-col gap-4">
      {d && (
        <KPIRow>
          <KPI label="Matching" value={(m.total ?? 0).toLocaleString()} hint={`${(d.stored_total ?? 0).toLocaleString()} stored`} />
          <KPI label="Delivered" value={m.delivered ?? 0} />
          <KPI label="Processed" value={m.processed ?? 0} />
          <KPI label="Bounced" value={m.bounced ?? 0} />
          <KPI label="Blocked" value={m.blocked ?? 0} />
          <KPI label="Dropped" value={m.dropped ?? 0} />
          <KPI label="Deferred" value={m.deferred ?? 0} />
        </KPIRow>
      )}
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      <Section title="SendGrid Email Logs"
        right={<div className="flex gap-2 items-center">
          <Btn small kind="primary" disabled={busy === 'pull'} onClick={pull}>
            {busy === 'pull' ? 'Pulling…' : 'Pull from SendGrid'}
          </Btn>
          <Btn small disabled={busy === 'local'} onClick={loadLocal}>
            {busy === 'local' ? 'Loading…' : 'Load from local database'}
          </Btn>
          <Btn small onClick={q.reload}>Reload</Btn>
        </div>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          SendGrid keeps only 30 days and the Email Logs add-on must be enabled; without it the
          pull falls back to our own webhook events.
          {d?.last_updated && ` Last pull ${fmtDate(d.last_updated)} (${d.last_count ?? 0} rows).`}
        </p>
        <div className="flex gap-2 flex-wrap items-end">
          <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
            Status
            <Select value={status} onChange={setStatus}
              options={['All', ...((d?.status_options || []) as string[])]} />
          </label>
          <Field label="Start date" type="date" value={start} onChange={setStart} />
          <Field label="End date" type="date" value={end} onChange={setEnd} />
          <Field label="Subject contains" value={subject} onChange={setSubject} />
          <Field label="Recipient contains" value={recipient} onChange={setRecipient} />
          <Field label="Sender contains" value={sender} onChange={setSender} />
          <Field label="Exclude recipients (comma separated)" value={exclude} onChange={setExclude} />
          <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
            Rows
            <Select value={limit} onChange={setLimit} options={['200', '1000', '5000']} />
          </label>
        </div>
        {q.error && <Msg kind="error">{q.error}</Msg>}
        {q.loading ? <Loading /> : (
          <DataTable rows={d?.rows || []} searchable csvName="sendgrid_email_logs"
            columns={['last_event_time', 'to_email', 'from_email', 'subject', 'status', 'reason',
              'opens_count', 'clicks_count', 'source', 'msg_id']}
            empty="The local SendGrid store is empty — pull from the API or load the local events." />
        )}
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Transactional template catalogue (read-only reference)
// ---------------------------------------------------------------------------
const catId = (c: any): string => c?.id ?? c?.key ?? '';
const catLabel = (c: any): string => c?.label ?? c?.title ?? catId(c);
const tplRecipient = (t: any): string => t?.recipient ?? t?.sent_to ?? '';

const gLine = (x: any): string => typeof x === 'string'
  ? x
  : [x?.icon, x?.name].filter(Boolean).join(' ') + (x?.desc ? ` — ${x.desc}` : '');

/**
 * Fire any transactional template at one address, with sample content.
 *
 * Reviewing copy used to mean reproducing the event that sends it — pay
 * something to see the receipt, cancel something to see the cancellation.
 * Single-recipient on purpose: a list here would make this a mailing tool
 * with no unsubscribe and no audit trail.
 */
const TemplateTrigger: React.FC = () => {
  const cat = useAdminGet('email', 'template_catalog');
  const list: any[] = (cat.data as any)?.templates || [];
  const [tpl, setTpl] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<Note>(null);

  useEffect(() => { if (!tpl && list.length) setTpl(list[0].key); }, [list, tpl]);

  const chosen = list.find(t => t.key === tpl);
  const labels: Record<string, string> = {};
  // Deprecated senders still exist and can still be fired, so the only thing
  // preventing a pointless test send is saying so in the option itself.
  list.forEach(t => {
    labels[t.key] = `${t.category} — ${t.label}${t.deprecated ? ' (deprecated)' : ''}`;
  });

  const send = async () => {
    setBusy(true); setNote(null);
    const { data, error } = await panelPost('email', 'send_template_preview',
      { template: tpl, to_email: to.trim() });
    setBusy(false);
    const d: any = data;
    if (error || !d?.success) {
      setNote({ kind: 'error', text: error || d?.error || 'Could not send that template.' });
    } else {
      setNote({ kind: 'ok', text: `Sent "${d.label}" to ${d.to}.` });
    }
  };

  const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to.trim());

  return (
    <Section title="Send a template for review"
      right={<Btn small onClick={cat.reload}>Refresh</Btn>}>
      <p className="text-xs text-grayscale-1100 -mt-1">
        Sends the real template with obviously-sample content, so you can read it
        in an inbox without having to trigger the event behind it.
      </p>
      {cat.error && <Msg kind="error">{cat.error}</Msg>}
      {cat.loading ? <Loading /> : (
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-grayscale-1100">Template</span>
            <Select value={tpl} onChange={setTpl}
              options={list.map(t => t.key)} labels={labels} />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-grayscale-1100">Send to</span>
            <TextInput value={to} onChange={setTo} placeholder="you@universitytech.io" />
          </div>
          <Btn onClick={send} disabled={busy || !valid || !tpl}>
            {busy ? 'Sending…' : 'Send test'}
          </Btn>
        </div>
      )}
      {chosen && (
        <div className="text-xs text-grayscale-1100">
          Normally sent to: {chosen.sent_to || '—'} · <code>{chosen.function}()</code>
          {String(chosen.category) === 'Notifications' && (
            <span className="text-amber-700">
              {' '}· this one is an internal notice, so it goes to the team inbox
              rather than the address above.
            </span>
          )}
        </div>
      )}
      {note && <Msg kind={note.kind}>{note.text}</Msg>}
    </Section>
  );
};

// The old subject+body editor for the five lifecycle keys lived here; it is
// replaced by email/EditableTemplates, which edits every field of every
// customer template (a second, partial editor on the same rows could save a
// subject/body and silently drop the sender and buttons beside them).

const TransactionalTemplates: React.FC = () => {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const [open, setOpen] = useState<string | null>(null);
  const res = useAdminGet('email', 'templates', { q, category: cat }, [q, cat]);
  const d: any = res.data;
  const cats: any[] = d?.categories || [];
  const allCats: any[] = d?.all_categories || cats;
  const labels: Record<string, string> = { all: 'All categories' };
  allCats.forEach(c => { labels[catId(c)] = `${c.icon || ''} ${catLabel(c)} (${c.count ?? (c.templates || []).length})`.trim(); });

  return (
    <div className="flex flex-col gap-4">
      <TemplateTrigger />
      <Section title="Platform e-mail catalogue — read-only reference"
        right={<div className="flex gap-2 items-center">
          <Select value={cat} onChange={setCat} options={['all', ...allCats.map(catId)]} labels={labels} />
          <Btn small onClick={res.reload}>Refresh</Btn>
        </div>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          {d?.source_note || 'The transactional emails the platform sends; the copy itself lives in db_ops.py.'}
          {d?.sendgrid && ` SendGrid ${d.sendgrid.configured ? 'is' : 'is NOT'} configured.`}
        </p>
        <TextInput value={q} onChange={setQ}
          placeholder="Search name / subject / function / recipient / body…" wide />
        {res.error && <Msg kind="error">{res.error}</Msg>}
        {typeof d?.match_count === 'number' && (
          <div className="text-xs text-grayscale-1100">
            {d.match_count} of {d.total_count ?? d.match_count} templates match.
          </div>
        )}
        {res.loading ? <Loading /> : cats.length === 0
          ? <div className="text-sm text-grayscale-1100 py-4 text-center">No template matches that search.</div>
          : (
            <div className="flex flex-col gap-4">
              {cats.map(c => (
                <div key={catId(c)} className="flex flex-col gap-2">
                  <div className="text-sm font-bold text-grayscale-1300 font-grotesk">
                    {c.icon} {c.subheader || catLabel(c)}
                  </div>
                  {c.note && <div className="text-xs text-grayscale-1100">{c.note}</div>}
                  <div className="grid md:grid-cols-2 gap-2">
                    {(c.templates || []).map((t: any) => (
                      <button key={t.id} onClick={() => setOpen(t.id)}
                        // Faded rather than hidden: these still exist in the
                        // code, so someone reading the panel needs to see them
                        // AND see that they are on the way out.
                        className={`text-left border rounded-xl p-3 flex flex-col gap-1 ${
                          t.deprecated
                            ? 'border-grayscale-300 bg-grayscale-100/60 opacity-70 hover:opacity-100'
                            : 'border-grayscale-300 bg-white hover:bg-grayscale-100'}`}>
                        <div className="text-sm font-bold text-grayscale-1300">
                          {t.name}
                          {t.deprecated && (
                            <span className="ml-2 align-middle text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                              deprecated
                            </span>)}
                        </div>
                        {t.deprecated && t.deprecated_note && (
                          <div className="text-[11px] text-amber-800">{t.deprecated_note}</div>
                        )}
                        <div className="text-xs text-grayscale-1100">{t.subject || '(subject in body)'}</div>
                        <div className="text-xs text-grayscale-1100">To: {tplRecipient(t) || '—'}</div>
                        <div className="text-xs text-grayscale-1200 font-mono">{t.function}</div>
                        {t.implemented === false && (
                          <div className="text-xs text-amber-700">documented, not implemented</div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
      </Section>

      {d?.guidelines && (
        <Section title="Template usage guidelines">
          <div className="grid md:grid-cols-2 gap-4 text-sm text-grayscale-1300">
            <div>
              <div className="text-xs uppercase tracking-wide text-grayscale-1100 mb-1">Best practices</div>
              <ul className="list-disc pl-5 flex flex-col gap-0.5">
                {(d.guidelines.best_practices || []).map((b: any, i: number) => <li key={i}>{gLine(b)}</li>)}
              </ul>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-grayscale-1100 mb-1">Categories</div>
              <ul className="list-disc pl-5 flex flex-col gap-0.5">
                {(d.guidelines.categories || []).map((b: any, i: number) => <li key={i}>{gLine(b)}</li>)}
              </ul>
            </div>
          </div>
          {d.guidelines.customizing && (
            <div className="border-t border-grayscale-300 pt-3 flex flex-col gap-2">
              <div className="text-sm text-grayscale-1300">{d.guidelines.customizing.intro}</div>
              <DataTable rows={d.guidelines.customizing.functions || []}
                columns={['fn', 'where', 'db_ops_line', 'exists']}
                labels={{ fn: 'Function', where: 'Documented at', db_ops_line: 'Actual line', exists: 'Exists' }}
                empty="No functions documented." />
              <ul className="list-disc pl-5 text-sm text-grayscale-1300 flex flex-col gap-0.5">
                {(d.guidelines.customizing.sendgrid || []).map((s: string, i: number) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
          {d.guidelines.source && <div className="text-xs text-grayscale-1100">{d.guidelines.source}</div>}
        </Section>
      )}

      {open && <TxTemplateDrawer id={open} onClose={() => setOpen(null)} />}
    </div>
  );
};

const TxTemplateDrawer: React.FC<{ id: string; onClose: () => void }> = ({ id, onClose }) => {
  const q = useAdminGet('email', 'template', { template_id: id }, [id]);
  const t: any = q.data || {};
  const line = (label: string, value: any) => value ? (
    <div className="flex gap-2 text-sm"><span className="text-grayscale-1100 w-28 shrink-0">{label}</span>
      <span className="text-grayscale-1300 break-words">{value}</span></div>
  ) : null;

  return (
    <Drawer title={t.name || id} onClose={onClose} wide
      subtitle={t.category_label ? `${t.category_icon || ''} ${t.category_label}` : undefined}>
      {q.error && <Msg kind="error">{q.error}</Msg>}
      {q.loading ? <Loading /> : (
        <>
          <Section title="What this email is">
            {line('Sent to', tplRecipient(t))}
            {line('Purpose', t.purpose)}
            {line('Trigger', t.trigger)}
            {line('Usage', t.usage)}
            {line('Subject', t.subject)}
            {line('From', t.from_line)}
            {line('Reply-to', t.reply_to)}
            {line('Type', t.type)}
            {line('BCC', t.bcc)}
          </Section>
          <Section title="Implementation">
            {t.implemented === false && (
              <Msg kind="error">
                This template is documented but no matching function exists in db_ops.py — nothing
                sends it today.
              </Msg>
            )}
            <DataTable rows={t.functions || []} columns={['name', 'exists', 'db_ops_line']}
              labels={{ name: 'Function', db_ops_line: 'db_ops.py line' }}
              empty={t.function ? `Documented as ${t.function}` : 'No function recorded.'} />
          </Section>
          <Section title="Body as the recipient sees it">
            <Pre>{t.body || '(no body recorded)'}</Pre>
          </Section>
        </>
      )}
    </Drawer>
  );
};

// ---------------------------------------------------------------------------
// OTP tools
// ---------------------------------------------------------------------------
const OtpTools: React.FC = () => {
  const [sendEmail, setSendEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [sendMsg, setSendMsg] = useState<Note>(null);
  const [sent, setSent] = useState<any>(null);

  const [vEmail, setVEmail] = useState('');
  const [code, setCode] = useState('');
  const [vConsent, setVConsent] = useState(false);
  const [vBusy, setVBusy] = useState(false);
  const [vMsg, setVMsg] = useState<Note>(null);
  const [verified, setVerified] = useState<any>(null);

  const send = async () => {
    setSendBusy(true); setSendMsg(null); setSent(null);
    const { data, error } = await panelPost('email', 'otp_send',
      { confirm: true, email: sendEmail.trim() });
    setSendBusy(false); setConsent(false);
    if (error) { setSendMsg({ kind: 'error', text: error }); return; }
    setSent(data);
    setVEmail(sendEmail.trim());
    setSendMsg({
      kind: 'ok',
      text: `OTP mailed to ${data.email}${data.expires_at ? `, expires ${fmtDate(data.expires_at)}` : ''}.`
        + (data.user_created ? ' A users row did not exist and was created.' : ''),
    });
  };

  const verify = async () => {
    setVBusy(true); setVMsg(null); setVerified(null);
    const { data, error } = await panelPost('email', 'otp_verify',
      { email: vEmail.trim(), code: code.trim() });
    setVBusy(false); setVConsent(false);
    if (error) { setVMsg({ kind: 'error', text: error }); return; }
    setVerified(data);
    setVMsg(data.success
      ? { kind: 'ok', text: `Code accepted — account verified=${String(data.verified)}, approved=${String(data.approved)}.` }
      : { kind: 'error', text: `${data.error || 'rejected'}${data.attempts != null ? ` (attempt ${data.attempts})` : ''}` });
  };

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Section title="Send a real login OTP">
        <p className="text-xs text-grayscale-1100 -mt-1">
          This mails an actual one-time code to reproduce a user's login problem. If the address
          has no account, one is created — exactly as the live login flow does. The code itself is
          never shown here; ask the user to read it back.
        </p>
        {sendMsg && <Msg kind={sendMsg.kind}>{sendMsg.text}</Msg>}
        <Field label="Email address" value={sendEmail} onChange={setSendEmail} type="email" />
        <SendGate label="Send OTP email" consent="I want to send a real OTP to this address."
          checked={consent} onChange={setConsent} busy={sendBusy} onSend={send}
          blocked={!sendEmail.includes('@') ? 'Enter a valid email address.' : undefined} />
        {sent && (
          <div className="text-xs text-grayscale-1100">
            user_id {sent.user_id || '—'} · conversation {sent.conversation_id || '—'}
            {sent.user_created && <span className="text-amber-700"> · account created by this send</span>}
          </div>
        )}
      </Section>

      <Section title="Verify an OTP">
        <p className="text-xs text-grayscale-1100 -mt-1">
          A correct code consumes the OTP and marks the account verified. It does not approve the
          account and does not create a session. Failure modes come back verbatim
          (user_not_found / no_active_otp / otp_expired / invalid_code).
        </p>
        {vMsg && <Msg kind={vMsg.kind}>{vMsg.text}</Msg>}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Email address" value={vEmail} onChange={setVEmail} type="email" />
          <Field label="6-digit code" value={code} onChange={setCode} placeholder="123456" />
        </div>
        <div className="flex flex-col gap-2 border-t border-grayscale-300 pt-3">
          <Check checked={vConsent} onChange={setVConsent}>
            I understand a correct code marks this account verified.
          </Check>
          <div>
            <Btn kind="primary" disabled={vBusy || !vConsent || !/^\d{6}$/.test(code.trim()) || !vEmail.includes('@')}
              onClick={verify}>
              {vBusy ? 'Verifying…' : 'Verify code'}
            </Btn>
          </div>
        </div>
        {verified && (
          <div className="text-xs text-grayscale-1100">
            user_id {verified.user_id || '—'} · verified {String(verified.verified)} ·
            approved {String(verified.approved)} · attempts {verified.attempts ?? '—'}
          </div>
        )}
      </Section>
    </div>
  );
};


// ---------------------------------------------------------------------------
// DELIVERABILITY — bounces and blocks, kept rather than lost
// ---------------------------------------------------------------------------
/**
 * SendGrid pushes bounce/block events to our webhook, but a webhook is
 * fire-and-forget: anything that arrives while the API is down is gone, and
 * nothing used to tell anyone a send had failed at all. The backend now files
 * every failure as an incident, mails a digest to info@universitytech.io, and
 * reconciles weekly against SendGrid's suppression lists.
 *
 * The digest is a digest on purpose — one bad campaign can bounce hundreds of
 * addresses in a minute, and that must not become hundreds of emails.
 */
/** One line over the Deliverability tab: is the daily SendGrid pull on, and
 *  is it actually running (last good run, next run, last error). */
const AutoPullLine: React.FC<{ s: any; busy: boolean; onRun: () => void }> = ({ s, busy, onRun }) => {
  const when = (v?: string | null) => (v ? new Date(v).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
  const lastOk = s.last_ok_at ? new Date(s.last_ok_at).getTime() : 0;
  const late = s.enabled && (!lastOk || Date.now() - lastOk > 2 * 86400000);
  const failed = !!s.last_error && (!s.last_ok_at || (s.last_try_at && s.last_try_at > s.last_ok_at));
  const tone = !s.enabled || late || failed
    ? 'text-amber-700 bg-amber-50 border-amber-200' : 'text-emerald-800 bg-emerald-50 border-emerald-200';
  return (
    <div className={`text-sm rounded-lg border px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap ${tone}`}>
      <div className="min-w-0">
        <b>Automatic pull from SendGrid: {s.enabled ? `on, every ${s.every_hours || 24} h` : 'OFF (EMAIL_HEALTH_SYNC=0)'}</b>
        {s.enabled && (
          <span>
            {' · '}last good run {s.last_ok_at ? when(s.last_ok_at) : 'never'}
            {s.next_due_at ? ` · next ${new Date(s.next_due_at).getTime() <= Date.now() + 60000 ? 'within the hour' : when(s.next_due_at)}` : ''}
          </span>
        )}
        {failed && <div className="text-xs mt-0.5">Last attempt failed: {s.last_error}</div>}
        {s.activity_log?.error && <div className="text-xs mt-0.5">Activity log: {s.activity_log.error}</div>}
      </div>
      <Btn small onClick={onRun} disabled={busy}>{busy ? 'Pulling…' : 'Run the automatic pull now'}</Btn>
    </div>
  );
};

const DAY_OPTIONS = ['7', '30', '90', '365'];

const Deliverability: React.FC<{ preset?: { q: string; days: string; nonce: string } | null }> = ({ preset }) => {
  const [days, setDays] = useState(preset?.days && DAY_OPTIONS.includes(preset.days) ? preset.days : '30');
  const [kind, setKind] = useState('All');
  const [q, setQ] = useState(preset?.q || '');
  const [note, setNote] = useState<Note>(null);
  const [busy, setBusy] = useState('');
  const [resolving, setResolving] = useState('');
  // A deep link (?tab=deliverability&q=…) prefills the incidents filter.
  useEffect(() => {
    if (!preset) return;
    setQ(preset.q);
    if (DAY_OPTIONS.includes(preset.days)) setDays(preset.days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset?.nonce]);

  const overview = useAdminGet('email', 'health_overview', { days }, [days, busy]);
  const rows = useAdminGet('email', 'health_incidents',
    { days, kind: kind === 'All' ? '' : kind.toLowerCase(), q, limit: 500 },
    [days, kind, q, busy]);
  const offenders = useAdminGet('email', 'health_offenders', { days: 90 }, [busy]);
  const runs = useAdminGet('email', 'health_sync_runs', { limit: 20 }, [busy]);
  // The automatic pull: on or off, last good run, next run (admin_email_health.schedule_status).
  const sched = useAdminGet('email', 'health_schedule', {}, [busy]);

  const t = overview.data?.totals || {};
  const n = (v: any) => (typeof v === 'number' ? v.toLocaleString() : v ?? 0);
  // What each failed mail was, whether the address has an account, and how
  // late the incident was filed (owner request 2026-10-05: an OTP bounce sat
  // 22 h before the daily sync filed it). Text fields so the CSV carries them.
  const incidentRows = useMemo(() => (rows.data?.rows || []).map((r: any) => ({
    ...r, account_text: r.account ? 'yes' : 'none', filed: filedText(r.source, r.filed_after_s),
  })), [rows.data]);

  const traceOf = (r: any) => (r.trace_link?.group ? r.trace_link
    : { group: 'email', tab: 'trace', email: r.email, incident: String(r.id) });
  const resolve = async (id: any) => {
    setResolving(String(id)); setNote(null);
    const { data, error } = await panelPost('email', 'health_resolve', { id });
    setResolving('');
    if (error || data?.success === false) {
      setNote({ kind: 'error', text: error || data?.error || 'Could not mark it resolved' });
      return;
    }
    rows.reload(); overview.reload();
  };

  const doPullLogs = async () => {
    setBusy('logs'); setNote(null);
    const { data, error } = await panelPost('email', 'health_sync_logs', { days: 14 });
    setBusy('');
    if (error || data?.success === false) {
      setNote({ kind: 'error', text: error || data?.error || 'Log pull failed' });
    } else {
      setNote({ kind: 'ok', text: `Pulled ${data.pulled ?? 0} message(s) from SendGrid's `
        + `activity log (${data.total ?? 0} matched). Existing rows were updated, not duplicated.` });
    }
  };

  const doSync = async () => {
    setBusy('sync'); setNote(null);
    const { data, error } = await panelPost('email', 'health_sync', { days: 14 });
    setBusy('');
    if (error || data?.success === false) {
      setNote({ kind: 'error', text: error || data?.error || 'Sync failed' });
    } else {
      setNote({ kind: 'ok', text: `Checked SendGrid: ${data.found} suppression record(s), ` +
        `${data.inserted} new.` + (data.errors?.length ? ` Warnings: ${data.errors.join('; ')}` : '') });
    }
  };

  const doAutoNow = async () => {
    setBusy('auto'); setNote(null);
    const { error } = await panelPost('email', 'health_auto_run', {});
    if (error) { setBusy(''); setNote({ kind: 'error', text: error }); return; }
    setNote({ kind: 'ok', text: 'Automatic pull started: failures and the activity log, back to the last good '
      + 'run. It takes up to a minute; the table below updates when it finishes.' });
    // It runs in the background: re-read the schedule and runs once it has had time.
    setTimeout(() => setBusy(''), 45000);
  };

  const doNotify = async () => {
    setBusy('notify'); setNote(null);
    const { data, error } = await panelPost('email', 'health_notify', {});
    setBusy('');
    if (error || data?.success === false) {
      setNote({ kind: 'error', text: error || data?.error || 'Digest could not be sent' });
    } else if (!data.sent) {
      setNote({ kind: 'ok', text: 'Nothing pending — no digest needed.' });
    } else {
      setNote({ kind: 'ok', text: `Digest for ${data.notified} incident(s) sent to ${data.recipient}.` });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {note && <Msg kind={note.kind}>{note.text}</Msg>}
      {overview.data && !overview.data.sendgrid_configured && (
        <Msg kind="error">No SendGrid API key configured, so the automatic pull can't run.</Msg>
      )}
      {sched.data && <AutoPullLine s={sched.data} busy={busy === 'auto'} onRun={doAutoNow} />}

      <KPIRow>
        <KPI label="Failures" value={n(t.incidents)} hint={`in the last ${days} days`} />
        <KPI label="Addresses" value={n(t.addresses)} />
        <KPI label="Not yet reported" value={n(t.pending)}
          hint="waiting for the next digest" />
        <KPI label="Unresolved" value={n(t.open)} />
      </KPIRow>

      <Section title="Reporting"
        right={<div className="flex gap-2">
          <Btn small onClick={doSync}>{busy === 'sync' ? 'Checking…' : 'Sync failures now'}</Btn>
          <Btn small onClick={doPullLogs}>{busy === 'logs' ? 'Pulling…' : 'Pull activity log now'}</Btn>
          <Btn small kind="primary" onClick={doNotify}>
            {busy === 'notify' ? 'Sending…' : 'Send digest now'}
          </Btn>
        </div>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          Digests go to <b>{overview.data?.recipient || 'info@universitytech.io'}</b>. Both
          jobs run automatically once a day, reaching back to the last good run (at least 14
          days, at most 30), and both de-duplicate — running them twice is harmless.
        </p>
        <p className="text-xs text-grayscale-1100 -mt-1">
          <b>Failures</b> reconciles against SendGrid's suppression lists (who bounced or was
          blocked). <b>Activity log</b> copies the full send history — delivered, opened,
          clicked — into our own table, because SendGrid deletes it after 30 days and it is
          gone for good once it ages out. That one needs the Email Activity add-on on the
          SendGrid account.
        </p>
        {runs.loading ? <Loading /> : (
          <DataTable rows={runs.data?.rows || []} csvName="email_sync_runs"
            columns={['started_at', 'finished_at', 'trigger', 'found', 'inserted', 'error']}
            empty="No reconcile has run yet." />
        )}
      </Section>

      <Section title="Failures by type">
        {overview.loading ? <Loading /> : (
          <BarList items={(overview.data?.by_kind || []).map((r: any) => ({
            label: r.kind, value: Number(r.n || 0),
          }))} />
        )}
      </Section>

      <Section title="Incidents"
        right={<div className="flex gap-2 items-center">
          <Select value={kind} onChange={setKind}
            options={['All', 'Bounce', 'Blocked', 'Dropped', 'Spamreport']} />
          <Select value={days} onChange={setDays} options={DAY_OPTIONS} />
          <TextInput value={q} onChange={setQ} placeholder="Filter address or reason…" />
        </div>}>
        {rows.error && <Msg kind="error">{rows.error}</Msg>}
        {rows.data?.truncated && (
          <p className="text-xs text-amber-700 -mt-1">
            Showing the {(rows.data.rows || []).length} most recent of {n(rows.data.total)} matches.
          </p>
        )}
        {/* Stale rows stay up while a reload (e.g. after Resolve) runs, so the table keeps its filters. */}
        {rows.loading && !rows.data ? <Loading /> : (
          <DataTable rows={incidentRows} searchable csvName="email_incidents"
            columns={['occurred_at', 'kind', 'email', 'source_label', 'account_text', 'reason', 'source', 'filed', 'notified_at']}
            labels={{ occurred_at: 'When', source_label: 'What it was', account_text: 'Account', filed: 'Filed',
              notified_at: 'Reported' }}
            render={{
              email: r => <AdminLink to={traceOf(r)} title="Trace this address">{r.email}</AdminLink>,
              reason: r => <span className="block max-w-[16rem] truncate">{r.reason || '—'}</span>,
              source_label: r => (r.source_label
                ? <span className="font-medium" title={r.source_key || undefined}>{r.source_label}</span>
                : <span className="text-grayscale-1100">—</span>),
              account_text: r => (r.account
                ? <AdminLink to={{ group: 'user360', email: r.account.email || r.email }} title="Open in User 360">yes</AdminLink>
                : <span className="text-grayscale-1100">none</span>),
              filed: r => (
                <span className={Number(r.filed_after_s) >= 3600 ? 'text-amber-700' : ''}
                  title={r.recorded_at ? `Filed ${fmtDate(r.recorded_at)}` : undefined}>{r.filed || '—'}</span>
              ),
            }}
            actions={r => (
              <div className="flex gap-1.5 justify-end items-center">
                <Btn small title="Everything about this address and this mail"
                  onClick={() => openAdmin(traceOf(r))}>
                  Trace
                </Btn>
                {r.resolved_at
                  ? <span className="text-[11px] text-grayscale-1100" title={fmtDate(r.resolved_at)}>resolved</span>
                  : <Btn small disabled={resolving === String(r.id)} onClick={() => resolve(r.id)}
                      title="Mark it dealt with. SendGrid's own suppression list is not changed.">
                      {resolving === String(r.id) ? 'Resolving…' : 'Resolve'}
                    </Btn>}
              </div>
            )}
            empty="No delivery failures in this window — good." />
        )}
      </Section>

      <Section title="Repeat offenders (90 days)">
        <p className="text-xs text-grayscale-1100 -mt-1">
          Addresses that keep failing. These are usually dead mailboxes still sitting on a
          list, and they're what damages sender reputation — worth removing at the source.
        </p>
        {offenders.loading ? <Loading /> : (
          <DataTable rows={offenders.data?.rows || []} searchable csvName="repeat_bouncers"
            columns={['email', 'failures', 'kinds', 'last_failure']}
            empty="No address has failed more than once." />
        )}
      </Section>
    </div>
  );
};

export default EmailPanel;
